import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const HOME_FALLBACK = {
  title: 'Creating Margin',
  description: 'Leading Digital Signage Solutions - Nationwide',
};

const SOCIAL_LINKS = [
  { label: 'Facebook', href: 'https://www.facebook.com/CreatingMargin' },
  { label: 'Twitter', href: 'https://twitter.com/CreatingMargin' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/creating-margin' },
  { label: 'Instagram', href: 'https://www.instagram.com/creatingmargin/' },
  { label: 'YouTube', href: 'https://www.youtube.com/channel/UCdyB4hxAGGX8IKFV7ot43xg/featured' },
];

const PORTFOLIO_PAGE_SIZE = 12;
const NEWS_PAGE_SIZE = 9;
const CONTACT_API_ENDPOINT = import.meta.env.VITE_CONTACT_API_ENDPOINT || '/api/contact';
const LazyNewsRoute = lazy(() => import('./routes/NewsRoute'));
const LazyPortfolioRoute = lazy(() => import('./routes/PortfolioRoute'));

const PAGE_HEADER_BACKGROUNDS = {
  '/about-us': {
    image: '/wp-content/uploads/2020/04/About_Us_Creating_Margin_Digital_Signage_Boise_ID.jpg',
    position: '100% 0%',
  },
  '/contact-us': {
    image: '/wp-content/uploads/2020/02/Contact_Us_Creating_Margin_Boise_Idaho.png',
    position: '100% 0%',
  },
  '/digital-signage': {
    image: '/wp-content/uploads/2020/04/Hyatt_Albuquerque_Digital_Signage_Wall_Mounted_Portrait.jpg',
    position: '100% 50%',
  },
  '/digital-signage/menu-boards': {
    image: '/wp-content/uploads/2020/04/Menu_Board_Restaurant_Display_Digital_Menu_Boise_Idaho-1.jpg',
    position: '100% 0%',
  },
  '/digital-signage/wayfinding': {
    image: '/wp-content/uploads/2020/02/Alamo_College_Interactive_Digital_Signage_San_Antonio.png',
    position: '100% 0%',
  },
  '/digital-signage/interactive-digital-signage': {
    image: '/wp-content/uploads/2020/04/Interactive_Digital_Signage_Touchscreen.jpg',
    position: '100% 0%',
  },
  '/digital-signage/video-walls': {
    image: '/wp-content/uploads/2020/01/Liberty_Global_Bank_Electronic_Signage_Denver_2.jpg',
    position: '100% 0%',
  },
  '/professional-services': {
    image: '/wp-content/uploads/2020/02/ProfessionalServicesHeader_813x647v2.png',
    position: '100% 50%',
  },
  '/professional-services/content-creation': {
    image: '/wp-content/uploads/2020/04/Digital_Signage_Content_Creation_Near_Me.jpg',
    position: '100% 0%',
  },
  '/professional-services/software-training': {
    image: '/wp-content/uploads/2020/01/GroupTrainingHeaderv2.png',
    position: '100% 0%',
  },
  '/professional-services/managed-services': {
    image: '/wp-content/uploads/2020/01/Header-For-Managed-Services_899x537.png',
    position: '100% 0%',
  },
  '/professional-services/video-production': {
    image: '/wp-content/uploads/2020/02/VideoProductionHeader_899x537.png',
    position: '100% 0%',
  },
  '/workplace-management': {
    image: '/wp-content/uploads/2020/04/Facility_Management_Digital_Signage_Workplace_Management.jpg',
    position: '100% 50%',
  },
  '/workplace-management/meeting-room-management': {
    image: '/wp-content/uploads/2020/02/MeetingRoomManagementHeader_800x433_FINAL.png',
    position: '100% 0%',
  },
  '/workplace-management/visitor-management': {
    image: '/wp-content/uploads/2020/02/VisitorManagementHeader_800x433.png',
    position: '100% 0%',
  },
  '/workplace-management/hot-desk-booking': {
    image: '/wp-content/uploads/2020/02/HotDeskBookingHeader_800x433.png',
    position: '100% 0%',
  },
};

const PAGE_HEADER_FALLBACKS = [
  {
    prefix: '/digital-signage',
    image: '/wp-content/uploads/2020/04/Hyatt_Albuquerque_Digital_Signage_Wall_Mounted_Portrait.jpg',
    position: '100% 50%',
  },
  {
    prefix: '/professional-services',
    image: '/wp-content/uploads/2020/02/ProfessionalServicesHeader_813x647v2.png',
    position: '100% 50%',
  },
  {
    prefix: '/workplace-management',
    image: '/wp-content/uploads/2020/04/Facility_Management_Digital_Signage_Workplace_Management.jpg',
    position: '100% 50%',
  },
];

const POST_HERO_IMAGE_FALLBACKS = {
  '/2022-year-in-review': '/wp-content/uploads/2022/12/pexels-pixabay-39511-scaled.jpg',
  '/2020-in-perspective': '/wp-content/uploads/2020/12/SideviewMirror_2020inPerspective.png',
  '/which-brightauthor-should-i-use': '/wp-content/uploads/2020/07/BAConnected-01-1024x520.png',
  '/covid-19-recovery-how-tracking-room-occupancy-can-help-ensure-safety': '/wp-content/uploads/2020/07/Density.io_.png',
  '/covid-19-and-the-rise-of-the-engaging-workplace-part-three': '/wp-content/uploads/2020/04/RiseOfTheEngagingWorkplacePt3.jpg',
  '/covid-19-and-the-rise-of-the-engaging-workplace-part-two': '/wp-content/uploads/2020/04/RiseOfTheEngagingWorkplacePt2.jpg',
  '/covid-19-and-the-rise-of-the-engaging-workplace': '/wp-content/uploads/2020/04/Rise-of-the-Engaging-Workplace.png',
};

const PPC_LANDING_PAGES = {
  '/lp/digital-signage-solutions': {
    slug: 'digital-signage-solutions',
    title: 'Digital Signage Solutions That Get Deployed Fast',
    subtitle:
      'From menu boards to video walls, we design, install, and support complete signage systems tailored to your locations.',
    primaryCtaLabel: 'Request A Free Consultation',
    secondaryCtaLabel: 'View Digital Signage Services',
    secondaryCtaHref: '/digital-signage/',
    heroImage: '/wp-content/uploads/2020/04/Hyatt_Albuquerque_Digital_Signage_Wall_Mounted_Portrait.jpg',
    offerHighlights: [
      'Turnkey hardware and software recommendations',
      'Content templates and scheduling support',
      'Rollout planning for single or multi-site deployments',
      'Training and managed services after launch',
    ],
    proofPoints: [
      'Hospitality, corporate, healthcare, and education deployments',
      'Nationwide implementation and remote support options',
      'Backed by in-house content and technical teams',
    ],
  },
  '/lp/menu-board-signage': {
    slug: 'menu-board-signage',
    title: 'Modern Digital Menu Boards For Restaurants And QSR',
    subtitle:
      'Replace static prints with dynamic menu displays that are easier to update, brand-aligned, and built for day-to-day operations.',
    primaryCtaLabel: 'Get Menu Board Pricing',
    secondaryCtaLabel: 'Explore Menu Board Solutions',
    secondaryCtaHref: '/digital-signage/menu-boards/',
    heroImage: '/wp-content/uploads/2020/10/illegal-petes-digital-menu.jpg',
    offerHighlights: [
      'Update pricing and promotions in minutes',
      'Templates designed for readability and speed of service',
      'Single-site and franchise-friendly management options',
      'Hardware guidance for indoor and drive-thru environments',
    ],
    proofPoints: [
      'Built for high-traffic service environments',
      'Creative support for seasonal and limited-time promotions',
      'Operator training included with handoff',
    ],
  },
  '/lp/workplace-management-software': {
    slug: 'workplace-management-software',
    title: 'Workplace Management Tools For Hybrid Teams',
    subtitle:
      'Improve room booking, visitor flow, and space utilization with practical workplace solutions your team can adopt quickly.',
    primaryCtaLabel: 'Book A Workplace Demo',
    secondaryCtaLabel: 'View Workplace Solutions',
    secondaryCtaHref: '/workplace-management/',
    heroImage: '/wp-content/uploads/2020/04/Facility_Management_Digital_Signage_Workplace_Management.jpg',
    offerHighlights: [
      'Meeting room booking and occupancy visibility',
      'Visitor check-in and welcome experience flows',
      'Digital signage integrations for workplace communications',
      'Support for phased pilot-to-rollout implementations',
    ],
    proofPoints: [
      'Designed for enterprise and multi-floor operations',
      'Integration-minded implementation approach',
      'Ongoing managed support available after go-live',
    ],
  },
  '/lp/managed-services-support': {
    slug: 'managed-services-support',
    title: 'Managed Digital Signage Support For Busy Teams',
    subtitle:
      'Keep your screens accurate, on-brand, and updated without overloading your internal staff. We become your execution partner.',
    primaryCtaLabel: 'Talk To Managed Services',
    secondaryCtaLabel: 'Explore Managed Services',
    secondaryCtaHref: '/professional-services/managed-services/',
    heroImage: '/wp-content/uploads/2020/01/Header-For-Managed-Services_899x537.png',
    offerHighlights: [
      'Monthly content updates and publishing support',
      'Playlist audits and quality control before deployment',
      'Priority troubleshooting for urgent display issues',
      'Clear reporting and planning for upcoming campaigns',
    ],
    proofPoints: [
      'Ideal for multi-location teams with limited bandwidth',
      'Reduces stale content and missed messaging windows',
      'Built around practical SLAs and reliable communication',
    ],
  },
  '/lp/digital-signage-content-studio': {
    slug: 'digital-signage-content-studio',
    title: 'Content Creation For Digital Signage That Drives Action',
    subtitle:
      'Get campaign-ready graphics, animations, and motion pieces tailored to your screens, audience, and brand standards.',
    primaryCtaLabel: 'Request Content Samples',
    secondaryCtaLabel: 'View Content Creation Services',
    secondaryCtaHref: '/professional-services/content-creation/',
    heroImage: '/wp-content/uploads/2020/04/Digital_Signage_Content_Creation_Near_Me.jpg',
    offerHighlights: [
      'Custom creative packages for promotions and evergreen messaging',
      'Templates that make future updates faster and more consistent',
      'Motion design for lobbies, menu boards, and video walls',
      'Creative aligned to your brand and communication goals',
    ],
    proofPoints: [
      'Built specifically for signage readability and dwell time',
      'Great fit for marketing teams without in-house motion capacity',
      'Supports one-off campaigns or ongoing monthly production',
    ],
  },
  '/lp/multi-location-signage-rollout': {
    slug: 'multi-location-signage-rollout',
    title: 'Scale Digital Signage Across Multiple Locations',
    subtitle:
      'Launch consistent signage standards across regions while keeping room for local messaging, promotions, and announcements.',
    primaryCtaLabel: 'Plan My Rollout',
    secondaryCtaLabel: 'See Enterprise Signage Solutions',
    secondaryCtaHref: '/digital-signage/',
    heroImage: '/wp-content/uploads/2020/04/Hyatt_Albuquerque_Digital_Signage_Wall_Mounted_Portrait.jpg',
    offerHighlights: [
      'Location-by-location rollout planning and sequencing',
      'Standardized hardware and software recommendations',
      'Governance model for brand-safe local content publishing',
      'Training and handoff playbooks for site managers',
    ],
    proofPoints: [
      'Designed for franchise, healthcare, and enterprise environments',
      'Minimizes deployment friction during expansion phases',
      'Creates repeatable systems your team can sustain long-term',
    ],
  },
  '/lp/interactive-wayfinding-kiosks': {
    slug: 'interactive-wayfinding-kiosks',
    title: 'Interactive Wayfinding Kiosks That Improve Visitor Flow',
    subtitle:
      'Help guests, patients, and visitors find the right destination quickly with touch-enabled wayfinding and on-screen guidance.',
    primaryCtaLabel: 'Get A Wayfinding Quote',
    secondaryCtaLabel: 'Explore Interactive Signage',
    secondaryCtaHref: '/digital-signage/interactive-digital-signage/',
    heroImage: '/wp-content/uploads/2020/04/Interactive_Digital_Signage_Touchscreen.jpg',
    offerHighlights: [
      'Touchscreen experience design for complex facilities',
      'Map-based navigation and destination search flows',
      'Brand-integrated interfaces built for public environments',
      'Support for campuses, healthcare, and corporate spaces',
    ],
    proofPoints: [
      'Reduces front-desk interruption and visitor confusion',
      'Improves first impressions for high-traffic environments',
      'Can be paired with broader digital signage ecosystems',
    ],
  },
  '/lp/employee-communications-signage': {
    slug: 'employee-communications-signage',
    title: 'Internal Communications Signage For Distributed Teams',
    subtitle:
      'Reach employees with timely, visible messaging across production floors, offices, break rooms, and common spaces.',
    primaryCtaLabel: 'Improve Internal Comms',
    secondaryCtaLabel: 'View Employee Communications Solutions',
    secondaryCtaHref: '/digital-signage/employee-communications/',
    heroImage: '/wp-content/uploads/2020/04/Facility_Management_Digital_Signage_Workplace_Management.jpg',
    offerHighlights: [
      'Safety, HR, KPI, and culture messaging templates',
      'Scheduling workflows for recurring and urgent updates',
      'Role-based content governance for different departments',
      'Creative and strategic support for adoption and engagement',
    ],
    proofPoints: [
      'Strong fit for manufacturing and operations-heavy organizations',
      'Improves visibility of priority updates in shared spaces',
      'Supports long-term communications consistency across sites',
    ],
  },
};

const AI_CONSULTING_PAGE = {
  path: '/ai-consulting',
  title: 'AI Consulting For Practical Business Operations',
  subtitle:
    'We help teams apply AI to real workflows across sales, service delivery, operations, and internal execution without hype or unnecessary complexity.',
  heroImage: '/wp-content/uploads/2020/02/ProfessionalServicesHeader_813x647v2.png',
  pillars: [
    {
      title: 'AI Readiness And Opportunity Mapping',
      description: 'Identify where AI can reduce manual workload and improve speed across your existing business processes.',
    },
    {
      title: 'Workflow Automation Sprints',
      description: 'Design and launch targeted automations for high-impact tasks in marketing, customer communication, and operations.',
    },
    {
      title: 'Team Enablement And Prompt Systems',
      description: 'Train teams on practical AI usage patterns with repeatable prompt libraries and governance-ready implementation guides.',
    },
  ],
  caseStudies: [
    {
      name: 'Creating Margin',
      logo: '/wp-content/uploads/2018/12/Creating-Margin.png',
      website: 'https://creatingmargin.com',
      focus: 'Marketing, implementation workflows, strategy, and delivery operations',
      summary:
        'Applied AI across site production, campaign execution, service strategy, and internal operations to reduce cycle time and improve delivery quality.',
      outcomes: [
        'Faster launch cycles for campaign and web updates',
        'Quicker internal drafting and review workflows',
        'Improved consistency in implementation playbooks',
        'Generative AI support for video creation and concept development',
        'AI-assisted graphic design ideation and production workflows',
        'AI-enhanced strategic planning and project management execution',
      ],
    },
    {
      name: 'Fresh House',
      logo: 'https://freshhousechecklist.com/footer-logo.png',
      website: 'https://freshhousechecklist.com',
      focus: 'Operational checklist systems and service workflow execution',
      summary:
        'Used AI-assisted systems to structure repeatable service operations, improve internal clarity, and streamline day-to-day business execution.',
      outcomes: [
        'Clearer SOP development and process documentation',
        'Reduced manual effort in recurring operational tasks',
        'More predictable team execution across workflows',
      ],
    },
    {
      name: 'Fresh Clothes',
      logo: 'https://freshclotheslaundry.com/images/logo-full.png',
      website: 'https://freshclotheslaundry.com',
      focus: 'Administrative software, operations throughput, and service integration',
      summary:
        'Built AI-supported and software-enabled workflows that help operations teams manage high workload and fast turnarounds while coordinating laundry and house cleaning services.',
      outcomes: [
        'Faster response drafting for inbound requests',
        'Improved consistency in customer-facing communication',
        'Smoother handoff between operational steps',
        'Administrative software workflows that support quick-turn operational execution',
        'Integrated service coordination between laundry operations and house cleaning operations',
        'Franchise management capabilities for repeatable multi-location operations',
      ],
    },
    {
      name: 'American Angus Foundation',
      logo: 'https://cd.angus.org/-/jssmedia/02ab1614f7f746cd8d364c387561a02c.png?mw=90&iar=0',
      website: 'https://www.angus.org/foundation',
      focus: 'Generative AI storytelling, restoration workflows, and interactive exhibit triggers',
      summary:
        'Leveraged Generative AI and video production to recreate a biographical video of George Grant from only a black-and-white photo, then integrated the experience into an interactive display activated by a physical button push.',
      outcomes: [
        'Recreated historical biography content from limited source material',
        'Combined AI-generated visuals with polished production workflows',
        'Delivered a button-triggered interactive display experience for on-site engagement',
      ],
    },
    {
      name: 'Antero Resources',
      logo: 'https://d1io3yog0oux5.cloudfront.net/_fbe594d572d1672630b3d26434dca8e2/anteroresources/files/theme/images/logo-sm.svg',
      website: 'https://www.anteroresources.com',
      focus: 'Generative AI scene development for digital signage content production',
      summary:
        'Used Generative AI to transform a still image of oil and gas related equipment into high-resolution video scenes designed for digital signage playback.',
      outcomes: [
        'Converted static equipment photography into dynamic high-resolution motion scenes',
        'Created signage-ready visual content aligned to industrial brand context',
        'Expanded reusable video assets for ongoing digital signage programming',
      ],
    },
    {
      name: 'Digital Signage Gear',
      logo: 'https://digitalsignagegear.com/wordpress-media/003-logo-transparent.png',
      website: 'https://digitalsignagegear.com',
      focus: 'Product workflow support and content operations',
      summary:
        'Applied AI assistance to support product-focused execution, content workflows, and operational throughput in a digital signage business context.',
      outcomes: [
        'Faster iteration on product and content support tasks',
        'Improved workflow clarity for repeatable execution',
        'Lower friction when scaling operational output',
      ],
    },
  ],
};

export default function App() {
  const location = useLocation();
  const [siteData, setSiteData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    fetch('/data/site-data.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load content (${response.status}).`);
        }

        return response.json();
      })
      .then((payload) => {
        if (active) {
          setSiteData(sanitizeSiteData(payload));
        }
      })
      .catch((loadError) => {
        if (active) {
          setError(loadError.message);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  const currentPath = normalizePath(location.pathname);
  const routeMap = useMemo(() => {
    if (!siteData) {
      return new Map();
    }

    return new Map([...siteData.pages, ...siteData.posts].map((entry) => [entry.path, entry]));
  }, [siteData]);

  const currentEntry = routeMap.get(currentPath) || null;
  const ppcLandingPage = PPC_LANDING_PAGES[currentPath] || null;
  const aiConsultingPage = currentPath === AI_CONSULTING_PAGE.path ? AI_CONSULTING_PAGE : null;
  const isPpcLandingPath = currentPath.startsWith('/lp/');
  const recentPosts = siteData?.posts?.slice(0, 3) ?? [];
  const socialLinks = getSiteSocialLinks(siteData?.site);
  const navigation = getEnhancedNavigation(siteData?.navigation || []);

  useSeoMetadata({
    locationPathname: location.pathname,
    siteData,
    currentPath,
    currentEntry,
    ppcLandingPage,
    aiConsultingPage,
    hasError: Boolean(error),
  });

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (!siteData && !error) {
      return;
    }

    document.dispatchEvent(new Event('react-content-ready'));
  }, [siteData, error, location.pathname]);

  if (error) {
    return (
      <Shell site={HOME_FALLBACK} navigation={[]} isPpcMinimal={false} socialLinks={SOCIAL_LINKS}>
        <StatusPanel eyebrow="Content load error" title={error} />
      </Shell>
    );
  }

  if (!siteData) {
    return null;
  }

  return (
    <Shell site={siteData.site} navigation={navigation} isPpcMinimal={isPpcLandingPath} socialLinks={socialLinks}>
      {ppcLandingPage ? (
        <PpcLandingPage page={ppcLandingPage} />
      ) : aiConsultingPage ? (
        <AiConsultingPage page={aiConsultingPage} />
      ) : currentEntry ? (
        currentPath === '/' ? (
          <HomePage entry={currentEntry} />
        ) : currentPath === '/news' ? (
          <Suspense fallback={<StatusPanel eyebrow="Loading" title="Loading news..." />}>
            <LazyNewsRoute
              entry={currentEntry}
              posts={siteData?.posts || []}
              newsPageSize={NEWS_PAGE_SIZE}
              cleanText={cleanText}
              formatDate={formatDate}
              getPageWindow={getPageWindow}
              postHeroFallbacks={POST_HERO_IMAGE_FALLBACKS}
              inferImageDimensionsFromUrl={inferImageDimensionsFromUrl}
              getResponsiveImageAttrs={getResponsiveImageAttrs}
            />
          </Suspense>
        ) : currentPath === '/portfolio' ? (
          <Suspense fallback={<StatusPanel eyebrow="Loading" title="Loading portfolio..." />}>
            <LazyPortfolioRoute
              entry={currentEntry}
              portfolioPageSize={PORTFOLIO_PAGE_SIZE}
              parsePortfolioEntry={parsePortfolioEntry}
              buildPortfolioSummary={buildPortfolioSummary}
              formatSlug={formatSlug}
              getPageWindow={getPageWindow}
              inferImageDimensionsFromUrl={inferImageDimensionsFromUrl}
            />
          </Suspense>
        ) : currentEntry.kind === 'page' ? (
          <MigratedPage entry={currentEntry} />
        ) : (
          <RoutePage entry={currentEntry} posts={recentPosts} />
        )
      ) : (
        <NotFound path={location.pathname} />
      )}
    </Shell>
  );
}

function Shell({ children, navigation, site, isPpcMinimal, socialLinks }) {
  const location = useLocation();
  const navRef = useRef(null);
  const [openGroup, setOpenGroup] = useState(null);

  useEffect(() => {
    setOpenGroup(null);
  }, [location.pathname]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenGroup(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenGroup(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <div className="site-frame">
        <header className="site-header">
          <Link className="site-logo" to="/" aria-label="Creating Margin home">
            <img src="/wp-content/uploads/2018/12/Creating-Margin.png" alt="Creating Margin" width="220" height="74" />
          </Link>
          {isPpcMinimal ? (
            <nav className="site-nav site-nav--minimal" aria-label="Landing page actions">
              <a className="header-cta header-cta--minimal" href="#ppc-lead-form">
                Request Consultation
              </a>
            </nav>
          ) : (
            <nav className="site-nav" aria-label="Main navigation" ref={navRef}>
              {navigation.map((group, index) => {
                const isOpen = openGroup === group.label;
                const panelId = `nav-panel-${index}`;

                return (
                  <details key={group.label} className="nav-group" open={isOpen}>
                    <summary
                      aria-expanded={isOpen ? 'true' : 'false'}
                      aria-controls={panelId}
                      onClick={(event) => {
                        event.preventDefault();
                        setOpenGroup((current) => (current === group.label ? null : group.label));
                      }}
                    >
                      {group.label}
                    </summary>
                    <div className="nav-panel" id={panelId}>
                      {group.children.map((item) => (
                        <Link key={item.href} to={item.href} onClick={() => setOpenGroup(null)}>
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </details>
                );
              })}
              <Link className="header-cta header-cta--blog" to="/news/">
                Blog
              </Link>
              <Link className="header-cta header-cta--contact" to="/contact-us/">
                Contact Us
              </Link>
            </nav>
          )}
        </header>

        <main className="site-main" id="main-content" tabIndex={-1}>
          {children}
        </main>

        {!isPpcMinimal ? (
          <footer className="site-footer">
            <div className="site-footer-brand">
              <img
                className="footer-logo"
                src="/wp-content/uploads/2018/12/Creating-Margin.png"
                alt="Creating Margin"
                width="220"
                height="74"
              />
              <p>
                We provide a unique blend of Content Creation, technical expertise in Training and
                Managed Services, and hardware components to make sure our customers quickly
                master the technologies we provide.
              </p>
            </div>

            <div className="site-footer-links-wrap">
              <p className="site-footer-heading">Explore</p>
              <div className="footer-links">
                <Link to="/about-us/">About</Link>
                <Link to="/portfolio/">Portfolio</Link>
                <Link to="/terms-and-conditions/">Terms</Link>
                <Link to="/contact-us/">Contact</Link>
              </div>
            </div>

            <div className="site-footer-meta">
              <p className="site-footer-heading">Follow Us</p>
              <div className="footer-links footer-socials">
                {socialLinks.map((item) => (
                  <a href={item.href} key={item.label} target="_blank" rel="noopener noreferrer" aria-label={item.label}>
                    {item.label}
                  </a>
                ))}
              </div>
              <p className="footer-copyright">© 2026 - All rights reserved by Creating Margin Corporation</p>
            </div>
          </footer>
        ) : null}
      </div>
    </div>
  );
}

function getSiteSocialLinks(site) {
  const configured = Array.isArray(site?.socialLinks)
    ? site.socialLinks
      .map((entry) => ({
        label: cleanText(entry?.label || ''),
        href: cleanText(entry?.href || ''),
      }))
      .filter((entry) => entry.label && entry.href)
    : [];

  return configured.length > 0 ? configured : SOCIAL_LINKS;
}

function getEnhancedNavigation(navigation) {
  if (!Array.isArray(navigation) || navigation.length === 0) {
    return navigation;
  }

  return navigation.map((group) => {
    const label = cleanText(group?.label).toLowerCase();
    const existingChildren = Array.isArray(group.children) ? group.children : [];

    if (label === 'services') {
      const hasAiLink = existingChildren.some((item) => normalizePath(item?.href || '') === AI_CONSULTING_PAGE.path);

      if (hasAiLink) {
        return group;
      }

      return {
        ...group,
        children: [
          ...existingChildren,
          {
            label: 'AI Consulting',
            href: AI_CONSULTING_PAGE.path,
          },
        ],
      };
    }

    if (label === 'company') {
      const hasNewsLink = existingChildren.some((item) => normalizePath(item?.href || '') === '/news');

      if (hasNewsLink) {
        return group;
      }

      return {
        ...group,
        children: [
          ...existingChildren,
          {
            label: 'News',
            href: '/news',
          },
        ],
      };
    }

    return group;
  });
}

function HomePage({ entry }) {
  useHomeInteractions(entry.path);
  const transformedHtml = useMemo(() => transformHomeMarkup(entry.html), [entry.html]);

  return (
    <>
      <section
        className="home-surface content-html"
        dangerouslySetInnerHTML={{ __html: transformedHtml }}
      />
    </>
  );
}

function MigratedPage({ entry }) {
  useHomeInteractions(entry.path);
  useShowcaseLightbox(entry.path);
  const pageClasses = getMigratedPageClasses(entry.path);
  const pageStyle = getMigratedPageStyle(entry.path);
  const transformedHtml = useMemo(() => transformMigratedMarkup(entry), [entry]);

  return (
    <>
      <section
        className={pageClasses}
        style={pageStyle}
        dangerouslySetInnerHTML={{ __html: transformedHtml }}
      />
    </>
  );
}

function PortfolioPage({ entry }) {
  const portfolio = useMemo(() => parsePortfolioEntry(entry), [entry]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);

  const filteredItems = useMemo(() => {
    if (activeCategory === 'all') {
      return portfolio.items;
    }

    return portfolio.items.filter((item) => item.categories.includes(activeCategory));
  }, [activeCategory, portfolio.items]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PORTFOLIO_PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PORTFOLIO_PAGE_SIZE;
  const pagedItems = filteredItems.slice(pageStart, pageStart + PORTFOLIO_PAGE_SIZE);
  const selectedIndex = filteredItems.findIndex((item) => item.id === selectedId);
  const selectedItem = selectedIndex >= 0 ? filteredItems[selectedIndex] : null;
  const selectedImageDimensions = inferImageDimensionsFromUrl(selectedItem?.fullImage || '');

  useEffect(() => {
    setCurrentPage(1);
    setSelectedId(null);
  }, [activeCategory]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (!selectedItem) {
      document.body.style.overflow = '';
      return undefined;
    }

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        setSelectedId(null);
      }

      if (event.key === 'ArrowRight' && filteredItems.length > 1) {
        setSelectedId(filteredItems[(selectedIndex + 1) % filteredItems.length].id);
      }

      if (event.key === 'ArrowLeft' && filteredItems.length > 1) {
        setSelectedId(filteredItems[(selectedIndex - 1 + filteredItems.length) % filteredItems.length].id);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeydown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [filteredItems, selectedIndex, selectedItem]);

  return (
    <>
      <section className="portfolio-page">
        <section className="portfolio-hero">
          <div className="portfolio-hero-copy">
            <p className="eyebrow">Portfolio</p>
            <h1>{portfolio.title}</h1>
            <p>{portfolio.description}</p>
          </div>

          <div className="portfolio-hero-stats">
            <div>
              <span>Examples</span>
              <strong>{portfolio.items.length}</strong>
            </div>
            <div>
              <span>Categories</span>
              <strong>{portfolio.categories.length - 1}</strong>
            </div>
            <div>
              <span>Page</span>
              <strong>
                {safePage} / {totalPages}
              </strong>
            </div>
          </div>
        </section>

        <section className="portfolio-browser">
          <div className="portfolio-browser-header">
            <div>
              <p className="eyebrow">Browse Examples</p>
              <h2>Filter by application and page through the work.</h2>
            </div>
            <p className="portfolio-results">
              Showing {filteredItems.length === 0 ? 0 : pageStart + 1}-{Math.min(pageStart + pagedItems.length, filteredItems.length)} of {filteredItems.length}
            </p>
          </div>

          <div className="portfolio-filters" aria-label="Portfolio categories">
            {portfolio.categories.map((category) => (
              <button
                key={category.slug}
                type="button"
                className={category.slug === activeCategory ? 'is-active' : ''}
                onClick={() => setActiveCategory(category.slug)}
              >
                <span>{category.label}</span>
                <small>{category.count}</small>
              </button>
            ))}
          </div>

          {pagedItems.length > 0 ? (
            <div className="portfolio-grid">
              {pagedItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="portfolio-card"
                  onClick={() => setSelectedId(item.id)}
                >
                  <div className="portfolio-card-image-wrap">
                    <img src={item.image} alt={item.alt} loading="lazy" />
                  </div>
                  <div className="portfolio-card-body">
                    <div className="portfolio-card-tags">
                      {item.categories.slice(0, 2).map((category) => (
                        <span key={category}>{portfolio.categoryLabels.get(category) || formatSlug(category)}</span>
                      ))}
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description || buildPortfolioSummary(item, portfolio.categoryLabels)}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="portfolio-empty">
              <h3>No examples match that category.</h3>
              <p>Choose another filter to view more Creating Margin portfolio examples.</p>
            </div>
          )}

          {totalPages > 1 ? (
            <div className="portfolio-pagination" aria-label="Portfolio pagination">
              <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={safePage === 1}>
                Previous
              </button>
              {getPageWindow(totalPages, safePage).map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  className={pageNumber === safePage ? 'is-active' : ''}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={safePage === totalPages}
              >
                Next
              </button>
            </div>
          ) : null}
        </section>

        <section className="portfolio-cta">
          <div>
            <p className="eyebrow">Need Something Similar?</p>
            <h2>Have additional questions that need answering?</h2>
            <p>We&apos;ll get back to you as soon as we can.</p>
          </div>
          <Link className="button-link" to={portfolio.ctaHref}>
            Contact Us
          </Link>
        </section>
      </section>

      {selectedItem ? (
        <div className="portfolio-modal" role="dialog" aria-modal="true" aria-label={selectedItem.title}>
          <button type="button" className="portfolio-modal-backdrop" onClick={() => setSelectedId(null)} aria-label="Close portfolio item" />
          <div className="portfolio-modal-panel">
            <button type="button" className="portfolio-modal-close" onClick={() => setSelectedId(null)} aria-label="Close portfolio item">
              Close
            </button>

            <div className="portfolio-modal-media">
              <img
                src={selectedItem.fullImage}
                alt={selectedItem.alt}
                width={selectedImageDimensions?.width}
                height={selectedImageDimensions?.height}
              />
            </div>

            <div className="portfolio-modal-copy">
              <div className="portfolio-card-tags">
                {selectedItem.categories.map((category) => (
                  <span key={category}>{portfolio.categoryLabels.get(category) || formatSlug(category)}</span>
                ))}
              </div>
              <h2>{selectedItem.title}</h2>
              <p>{selectedItem.description || buildPortfolioSummary(selectedItem, portfolio.categoryLabels)}</p>
              <div className="portfolio-modal-actions">
                <a href={selectedItem.fullImage} target="_blank" rel="noopener noreferrer">
                  Open Full Image
                </a>
                {filteredItems.length > 1 ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setSelectedId(filteredItems[(selectedIndex - 1 + filteredItems.length) % filteredItems.length].id)}
                    >
                      Previous Example
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedId(filteredItems[(selectedIndex + 1) % filteredItems.length].id)}
                    >
                      Next Example
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function PpcLandingPage({ page }) {
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusKind, setStatusKind] = useState('');
  const heroImageDimensions = inferImageDimensionsFromUrl(page?.heroImage || '');
  const heroImageResponsive = getResponsiveImageAttrs(page?.heroImage || '', '(max-width: 980px) 100vw, 42vw');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const form = event.currentTarget;
    const formData = new FormData(form);

    const payload = {
      firstName: cleanText(String(formData.get('firstName') || '')),
      lastName: cleanText(String(formData.get('lastName') || '')),
      email: cleanText(String(formData.get('email') || '')),
      message: cleanText(String(formData.get('message') || '')),
      honeypot: cleanText(String(formData.get('companyWebsite') || '')),
      formType: `PPC Lead - ${page.slug}`,
      pagePath: normalizePath(location.pathname),
    };

    if (!payload.firstName || !payload.lastName || !payload.email || !payload.message) {
      setStatusKind('is-error');
      setStatusMessage('Please complete all required fields before submitting.');
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(payload.email)) {
      setStatusKind('is-error');
      setStatusMessage('Please provide a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setStatusKind('is-loading');
    setStatusMessage('Sending your request...');

    try {
      const response = await fetch(CONTACT_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const contentType = response.headers.get('content-type') || '';
      const body = contentType.includes('application/json') ? await response.json().catch(() => null) : null;

      if (!response.ok) {
        throw new Error(body?.error || 'Unable to submit right now.');
      }

      if (!body?.ok) {
        throw new Error('Lead form service is not configured yet.');
      }

      setStatusKind('is-success');
      setStatusMessage('Thanks. Your request was sent and our team will follow up shortly.');
      form.reset();
    } catch (error) {
      setStatusKind('is-error');
      setStatusMessage(error instanceof Error ? error.message : 'Unable to submit right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="ppc-landing" aria-label="PPC landing page">
      <div className="ppc-landing__hero">
        <div className="ppc-landing__copy">
          <h1>{page.title}</h1>
          <p className="ppc-landing__subtitle">{page.subtitle}</p>
          <div className="ppc-landing__actions">
            <a href="#ppc-lead-form" className="ppc-landing__cta ppc-landing__cta--primary">
              {page.primaryCtaLabel}
            </a>
            <Link to={page.secondaryCtaHref} className="ppc-landing__cta ppc-landing__cta--ghost">
              {page.secondaryCtaLabel}
            </Link>
          </div>
          <ul className="ppc-landing__highlights">
            {page.offerHighlights.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="ppc-landing__media">
          <img
            src={page.heroImage}
            alt={page.title}
            loading="eager"
            width={heroImageDimensions?.width}
            height={heroImageDimensions?.height}
            srcSet={heroImageResponsive.srcSet}
            sizes={heroImageResponsive.sizes}
          />
        </div>
      </div>

      <section className="ppc-landing__proof" aria-label="Why teams choose Creating Margin">
        {page.proofPoints.map((point) => (
          <article key={point} className="ppc-landing__proof-card">
            <h2>{point}</h2>
          </article>
        ))}
      </section>

      <section className="ppc-landing__form-wrap" id="ppc-lead-form">
        <div className="ppc-landing__form-copy">
          <p className="ppc-landing__eyebrow">Get A Fast Response</p>
          <h2>Tell us what you need and we will contact you.</h2>
          <p>
            Share your project goals, locations, and timeline. Our team will follow up with practical next steps.
          </p>
        </div>

        <form className="ppc-landing__form" onSubmit={handleSubmit} noValidate>
          <div className="ppc-landing__grid">
            <label>
              First Name
              <input type="text" name="firstName" autoComplete="given-name" required />
            </label>
            <label>
              Last Name
              <input type="text" name="lastName" autoComplete="family-name" required />
            </label>
            <label className="ppc-landing__grid-span">
              Work Email
              <input type="email" name="email" autoComplete="email" required />
            </label>
            <label className="ppc-landing__grid-span">
              Project Details
              <textarea name="message" rows={5} required />
            </label>
            <label className="ppc-landing__honeypot" aria-hidden="true">
              Company Website
              <input type="text" name="companyWebsite" tabIndex={-1} autoComplete="off" />
            </label>
          </div>

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : page.primaryCtaLabel}
          </button>

          {statusMessage ? <p className={`ppc-landing__status ${statusKind}`} role="status" aria-live="polite">{statusMessage}</p> : null}
        </form>
      </section>
    </section>
  );
}

function AiConsultingPage({ page }) {
  const heroStyle = page.heroImage ? { '--ai-hero-image': `url("${page.heroImage}")` } : undefined;

  return (
    <section className="ai-consulting" aria-label="AI consulting services">
      <section className="ai-consulting__hero ai-consulting__hero--banner" style={heroStyle}>
        <p className="eyebrow">AI Consulting</p>
        <h1>{page.title}</h1>
        <p>{page.subtitle}</p>
        <div className="ai-consulting__hero-actions">
          <Link className="button-link" to="/contact-us/">
            Book An AI Strategy Call
          </Link>
          <Link className="ai-consulting__secondary-cta" to="/professional-services/">
            Explore Professional Services
          </Link>
        </div>
      </section>

      <section className="ai-consulting__pillars" aria-label="AI consulting offers">
        {page.pillars.map((pillar) => (
          <article key={pillar.title} className="ai-consulting__pillar-card">
            <h2>{pillar.title}</h2>
            <p>{pillar.description}</p>
          </article>
        ))}
      </section>

      <section className="ai-consulting__cases" aria-label="Case studies from related businesses">
        <div className="section-heading">
          <p className="eyebrow">Experience-Based Case Studies</p>
          <h2>Applied Across Six Different Businesses</h2>
        </div>

        <div className="ai-consulting__case-grid">
          {page.caseStudies.map((study) => (
            <article key={study.name} className="ai-consulting__case-card">
              <div className="ai-consulting__case-top">
                {study.logo && (
                  <div className="ai-consulting__case-logo-wrap">
                    <img
                      src={study.logo}
                      alt={`${study.name} logo`}
                      className="ai-consulting__case-logo"
                      width={inferImageDimensionsFromUrl(study.logo)?.width}
                      height={inferImageDimensionsFromUrl(study.logo)?.height}
                    />
                  </div>
                )}
                <div className="ai-consulting__case-top-row">
                  <h3>{study.name}</h3>
                  <a href={study.website} target="_blank" rel="noopener noreferrer">
                    Visit Site
                  </a>
                </div>
              </div>
              <p className="ai-consulting__case-focus">Focus: {study.focus}</p>
              <p>{study.summary}</p>
              <ul>
                {study.outcomes.map((outcome) => (
                  <li key={outcome}>{outcome}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="ai-consulting__cta">
        <h2>Want To Build AI Workflows Inside Your Business?</h2>
        <p>
          We can help modernize your business overall by mapping practical opportunities, launching targeted automations,
          accelerating rapid application programming, and delivering content creation support while training your team
          to run these systems with confidence.
        </p>
        <Link className="button-link" to="/contact-us/">
          Start With A Consultation
        </Link>
      </section>
    </section>
  );
}

function NewsPage({ entry, posts }) {
  const newsIntro =
    'Get updates on digital signage strategy, workplace communication trends, and practical implementation insights from the Creating Margin team.';

  const [currentPage, setCurrentPage] = useState(1);

  const sortedPosts = useMemo(() => {
    return [...posts].sort((left, right) => {
      const leftTime = Date.parse(left?.date || '') || 0;
      const rightTime = Date.parse(right?.date || '') || 0;
      return rightTime - leftTime;
    });
  }, [posts]);

  const totalPages = Math.max(1, Math.ceil(sortedPosts.length / NEWS_PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * NEWS_PAGE_SIZE;
  const pagedPosts = sortedPosts.slice(pageStart, pageStart + NEWS_PAGE_SIZE);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const getNewsSummary = (post) => {
    const summary = cleanText(post?.excerpt || '');
    if (!summary) {
      return 'Open article';
    }

    return summary.length > 170 ? `${summary.slice(0, 167).trimEnd()}...` : summary;
  };

  const getPostCardImage = (post) => {
    const mediaImage = Array.isArray(post?.media) && post.media.length > 0 ? post.media[0] : '';
    if (mediaImage) {
      return mediaImage;
    }

    const html = String(post?.html || '');
    const imageMatch = html.match(/<img[^>]+src=(['"])(.*?)\1/i);
    if (imageMatch?.[2]) {
      return imageMatch[2];
    }

    if (post?.path && POST_HERO_IMAGE_FALLBACKS[post.path]) {
      return POST_HERO_IMAGE_FALLBACKS[post.path];
    }

    return '/wp-content/uploads/2018/12/Creating-Margin.png';
  };

  return (
    <>
      <section className="news-index-hero">
        <p className="eyebrow">From the blog</p>
        <h1>{entry?.title || 'News'}</h1>
        <p>{newsIntro}</p>
      </section>

      <section className="news-index-list">
        <div className="section-heading">
          <h2>Latest Posts</h2>
        </div>
        <div className="post-grid post-grid--news">
          {pagedPosts.map((post) => {
            const image = getPostCardImage(post);
            const imageDimensions = inferImageDimensionsFromUrl(image);

            return (
              <Link className="post-card post-card--news" to={post.path} key={post.path}>
                <div className="post-card__media">
                  <img src={image} alt={post.title} loading="lazy" width={imageDimensions?.width} height={imageDimensions?.height} />
                </div>
                <p>{formatDate(post.date)}</p>
                <h3>{post.title}</h3>
                <span>{getNewsSummary(post)}</span>
              </Link>
            );
          })}
        </div>
        {totalPages > 1 ? (
          <div className="portfolio-pagination news-pagination" aria-label="News pagination">
            <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={safePage === 1}>
              Previous
            </button>
            {getPageWindow(totalPages, safePage).map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                className={pageNumber === safePage ? 'is-active' : ''}
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={safePage === totalPages}
            >
              Next
            </button>
          </div>
        ) : null}
      </section>
    </>
  );
}

function RoutePage({ entry, posts }) {
  const articleClassName = entry.kind === 'post' ? 'route-article route-article--post content-html' : 'route-article content-html';
  const hasInlineImage = /<img[\s>]/i.test(String(entry?.html || ''));
  const fallbackImage =
    (Array.isArray(entry?.media) && entry.media.length > 0 ? entry.media[0] : '') ||
    POST_HERO_IMAGE_FALLBACKS[entry?.path || ''] ||
    '';
  const fallbackImageDimensions = inferImageDimensionsFromUrl(fallbackImage);
  const fallbackImageResponsive = getResponsiveImageAttrs(fallbackImage, '(max-width: 980px) 100vw, 72vw');
  const showFallbackImage = entry.kind === 'post' && !hasInlineImage && Boolean(fallbackImage);

  return (
    <>
      <section className="route-hero">
        <div>
          <p className="eyebrow">{entry.kind === 'post' ? 'News article' : 'Page'}</p>
          <h1>{entry.title}</h1>
          <p>{entry.excerpt || 'Migrated content from the Creating Margin WordPress site.'}</p>
        </div>
        <dl className="route-meta">
          <div>
            <dt>Published</dt>
            <dd>{formatDate(entry.date)}</dd>
          </div>
          <div>
            <dt>Updated</dt>
            <dd>{formatDate(entry.modified || entry.date)}</dd>
          </div>
        </dl>
      </section>

      <section className="route-layout">
        <article className={articleClassName}>
          {showFallbackImage ? (
            <figure className="post-lead-media">
              <img
                src={fallbackImage}
                alt={entry.title}
                loading="eager"
                width={fallbackImageDimensions?.width}
                height={fallbackImageDimensions?.height}
                srcSet={fallbackImageResponsive.srcSet}
                sizes={fallbackImageResponsive.sizes}
              />
            </figure>
          ) : null}
          <div dangerouslySetInnerHTML={{ __html: entry.html }} />
        </article>
        <aside className="route-sidebar">
          <div className="sidebar-panel">
            <p className="eyebrow">Creating Margin</p>
            <h2>Need a custom solution?</h2>
            <p>
              Reach out for digital signage consulting, content creation, software training,
              and hardware planning.
            </p>
            <Link className="button-link" to="/contact-us/">
              Contact Us
            </Link>
          </div>
          {entry.embeds.length > 0 ? (
            <div className="sidebar-panel">
              <p className="eyebrow">Embeds</p>
              <div className="embed-list">
                {entry.embeds.map((embed) => (
                  <a href={embed} key={embed} target="_blank" rel="noopener noreferrer">
                    {embed}
                  </a>
                ))}
              </div>
            </div>
          ) : null}
        </aside>
      </section>

      <RecentPosts posts={posts} />
    </>
  );
}

function RecentPosts({ posts }) {
  if (posts.length === 0) {
    return null;
  }

  return (
    <section className="recent-posts">
      <div className="section-heading">
        <p className="eyebrow">From the blog</p>
        <h2>Recent Creating Margin posts</h2>
      </div>
      <div className="post-grid">
        {posts.map((post) => (
          <Link className="post-card" to={post.path} key={post.path}>
            <p>{formatDate(post.date)}</p>
            <h3>{post.title}</h3>
            <span>{post.excerpt || 'Open article'}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function StatusPanel({ eyebrow, title }) {
  return (
    <section className="status-panel">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
    </section>
  );
}

function NotFound({ path }) {
  return (
    <section className="status-panel">
      <p className="eyebrow">404</p>
      <h1>That page was not found in the migrated export.</h1>
      <p>The route {path} does not exist in the published WordPress content export.</p>
      <Link className="button-link" to="/">
        Return home
      </Link>
    </section>
  );
}

function useHomeInteractions(key) {
  useEffect(() => {
    const cleanups = [];
    const applyLegacyVisibility = (targets, isVisible) => {
      targets.forEach((targetId) => {
        const element = document.getElementById(targetId);
        if (!element) {
          return;
        }

        if (!element.dataset.legacyDisplay) {
          const computedDisplay = window.getComputedStyle(element).display;
          element.dataset.legacyDisplay = computedDisplay && computedDisplay !== 'none' ? computedDisplay : 'block';
        }

        element.style.display = isVisible ? element.dataset.legacyDisplay : 'none';
        element.hidden = !isVisible;
        element.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      });
    };

    const previousShowElements = window.showElements;
    const previousHideElements = window.hideElements;

    window.showElements = (targets) => applyLegacyVisibility(normalizeLegacyTargets(targets), true);
    window.hideElements = (targets) => applyLegacyVisibility(normalizeLegacyTargets(targets), false);

    cleanups.push(() => {
      if (previousShowElements) {
        window.showElements = previousShowElements;
      } else {
        delete window.showElements;
      }

      if (previousHideElements) {
        window.hideElements = previousHideElements;
      } else {
        delete window.hideElements;
      }
    });

    const root = document.querySelector('.home-surface, .page-surface');
    if (!root) {
      return () => {
        cleanups.forEach((cleanup) => cleanup());
      };
    }

    const legacyButtons = Array.from(root.querySelectorAll('[data-legacy-action][data-legacy-targets]'));

    legacyButtons.forEach((button) => {
      const action = button.getAttribute('data-legacy-action');
      const targets = normalizeLegacyTargets(button.getAttribute('data-legacy-targets'));

      if (action === 'show-elements') {
        applyLegacyVisibility(targets, false);
      }

      const handler = (event) => {
        event.preventDefault();

        if (action === 'show-elements') {
          applyLegacyVisibility(targets, true);
          button.style.display = 'none';
          button.hidden = true;
          return;
        }

        if (action === 'hide-elements') {
          applyLegacyVisibility(targets, false);
        }
      };

      button.addEventListener('click', handler);
      cleanups.push(() => button.removeEventListener('click', handler));
    });

    const tabRoots = Array.from(root.querySelectorAll('.vc_tta'));

    tabRoots.forEach((tabRoot) => {
      const panelLinks = Array.from(tabRoot.querySelectorAll('.vc_tta-tab a, .vc_tta-panel-title a, .vc_tta-panel-heading a'));
      const tabs = Array.from(tabRoot.querySelectorAll('.vc_tta-tab'));
      const panels = Array.from(tabRoot.querySelectorAll('.vc_tta-panel'));

      if (panelLinks.length === 0 || tabs.length === 0 || panels.length === 0) {
        return;
      }

      const activatePanel = (hash) => {
        tabs.forEach((tab) => {
          const link = tab.querySelector('a');
          tab.classList.toggle('vc_active', link?.getAttribute('href') === hash);
        });

        panels.forEach((panel) => {
          panel.classList.toggle('vc_active', `#${panel.id}` === hash);
        });
      };

      panelLinks.forEach((link) => {
        const handler = (event) => {
          const href = link.getAttribute('href');
          if (!href || !href.startsWith('#')) {
            return;
          }

          event.preventDefault();
          activatePanel(href);
        };

        link.addEventListener('click', handler);
        cleanups.push(() => link.removeEventListener('click', handler));
      });
    });

    if (key.startsWith('/contact-us')) {
      // Load reCAPTCHA v3 script if not already loaded
      if (!window.grecaptcha) {
        const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
        const script = document.createElement('script');
        script.src = siteKey
          ? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
          : 'https://www.google.com/recaptcha/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }

      const forms = Array.from(root.querySelectorAll('#contact_form .frm-fluent-form'));

      forms.forEach((form, index) => {
        const submitButton = form.querySelector('button[type="submit"], .ff-btn-submit');
        const statusNode = document.createElement('p');
        statusNode.className = 'contact-form-status';
        statusNode.setAttribute('aria-live', 'polite');
        statusNode.hidden = true;

        const submitWrapper = form.querySelector('.ff_submit_btn_wrapper');
        (submitWrapper || form).appendChild(statusNode);

        const setStatus = (kind, message) => {
          statusNode.hidden = !message;
          statusNode.textContent = message || '';
          statusNode.classList.remove('is-error', 'is-success', 'is-loading');

          if (kind) {
            statusNode.classList.add(kind);
          }
        };

        const handler = async (event) => {
          event.preventDefault();

          const formData = new FormData(form);
          const formType = index === 0 ? 'Support and Managed Services' : 'Sales Inquiries';
          const payload = {
            firstName: cleanText(String(formData.get('input_text') || '')),
            lastName: cleanText(String(formData.get('input_text_2') || '')),
            email: cleanText(String(formData.get('email_1') || '')),
            message: cleanText(String(formData.get('description') || '')),
            honeypot: cleanText(String(formData.get('item_3__fluent_sf') || '')),
            formType,
            pagePath: key,
          };

          if (!payload.firstName || !payload.lastName || !payload.email || !payload.message) {
            setStatus('is-error', 'Please complete all fields before sending your message.');
            return;
          }

          if (!/^\S+@\S+\.\S+$/.test(payload.email)) {
            setStatus('is-error', 'Please enter a valid email address.');
            return;
          }

          submitButton?.setAttribute('disabled', 'true');
          setStatus('is-loading', 'Sending your message...');

          try {
            // Get reCAPTCHA v3 token
            let recaptchaToken = '';
            if (window.grecaptcha) {
              const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
              if (siteKey) {
                recaptchaToken = await window.grecaptcha.execute(siteKey, { action: 'submit' });
              }
            }

            payload.recaptchaToken = recaptchaToken;

            const response = await fetch(CONTACT_API_ENDPOINT, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify(payload),
            });

            const contentType = response.headers.get('content-type') || '';
            const body = contentType.includes('application/json') ? await response.json().catch(() => null) : null;

            if (!response.ok) {
              throw new Error(body?.error || 'Unable to send message right now.');
            }

            if (!body?.ok) {
              throw new Error('Email service is not configured yet.');
            }

            setStatus('is-success', 'Thanks, your message has been sent. We will reply soon.');
            form.reset();
          } catch (submissionError) {
            const fallback = 'We could not send your message right now. Please try again in a minute.';
            setStatus('is-error', submissionError instanceof Error ? submissionError.message || fallback : fallback);
          } finally {
            submitButton?.removeAttribute('disabled');
          }
        };

        form.addEventListener('submit', handler);
        cleanups.push(() => {
          form.removeEventListener('submit', handler);
          statusNode.remove();
        });
      });
    }

    // Handle fluent forms on non-contact pages (e.g., audits)
    const standaloneFluentForms = Array.from(root.querySelectorAll('.frm-fluent-form')).filter(
      (form) => !form.closest('#contact_form'),
    );

    if (standaloneFluentForms.length > 0 && !window.grecaptcha) {
      const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
      const script = document.createElement('script');
      script.src = siteKey
        ? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
        : 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    standaloneFluentForms.forEach((form) => {
      const submitButton = form.querySelector('button[type="submit"], .ff-btn-submit');
      const statusNode = document.createElement('p');
      statusNode.className = 'contact-form-status';
      statusNode.setAttribute('aria-live', 'polite');
      statusNode.hidden = true;

      const submitWrapper = form.querySelector('.ff_submit_btn_wrapper');
      (submitWrapper || form).appendChild(statusNode);

      const setStatus = (kind, message) => {
        statusNode.hidden = !message;
        statusNode.textContent = message || '';
        statusNode.classList.remove('is-error', 'is-success', 'is-loading');
        if (kind) statusNode.classList.add(kind);
      };

      const handler = async (event) => {
        event.preventDefault();

        const formData = new FormData(form);
        const firstName = cleanText(String(formData.get('names[first_name]') || ''));
        const lastName = cleanText(String(formData.get('names[last_name]') || ''));
        const email = cleanText(String(formData.get('email') || ''));
        const message = cleanText(String(formData.get('message') || ''));
        const phone = cleanText(String(formData.get('numeric-field') || ''));
        const numberOfDevices = cleanText(String(formData.get('input_text') || ''));
        const hasSignage = cleanText(String(formData.get('input_radio') || ''));
        const hasCMS = cleanText(String(formData.get('input_radio_1') || ''));
        const honeypot = cleanText(String(formData.get('item__1__fluent_checkme_') || ''));

        if (!firstName || !lastName || !email || !message) {
          setStatus('is-error', 'Please complete all required fields before sending.');
          return;
        }

        if (!/^\S+@\S+\.\S+$/.test(email)) {
          setStatus('is-error', 'Please enter a valid email address.');
          return;
        }

        if (honeypot) return;

        submitButton?.setAttribute('disabled', 'true');
        setStatus('is-loading', 'Sending your message...');

        const extraDetails = [
          phone ? `Phone: ${phone}` : '',
          hasSignage ? `Digital signage in place: ${hasSignage}` : '',
          hasCMS ? `CMS in use: ${hasCMS}` : '',
          numberOfDevices ? `Number of devices: ${numberOfDevices}` : '',
        ].filter(Boolean).join('\n');

        const payload = {
          firstName,
          lastName,
          email,
          message: extraDetails ? `${message}\n\n${extraDetails}` : message,
          formType: 'Digital Signage Audit',
          pagePath: key,
        };

        try {
          let recaptchaToken = '';
          if (window.grecaptcha) {
            const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
            if (siteKey) {
              recaptchaToken = await window.grecaptcha.execute(siteKey, { action: 'submit' });
            }
          }
          payload.recaptchaToken = recaptchaToken;

          const response = await fetch(CONTACT_API_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });

          const contentType = response.headers.get('content-type') || '';
          const body = contentType.includes('application/json') ? await response.json().catch(() => null) : null;

          if (!response.ok) {
            throw new Error(body?.error || 'Unable to send message right now.');
          }

          if (!body?.ok) {
            throw new Error('Email service is not configured yet.');
          }

          setStatus('is-success', 'Thanks! Your message has been sent. We will reply soon.');
          form.reset();
        } catch (submissionError) {
          const fallback = 'We could not send your message right now. Please try again in a minute.';
          setStatus('is-error', submissionError instanceof Error ? submissionError.message || fallback : fallback);
        } finally {
          submitButton?.removeAttribute('disabled');
        }
      };

      form.addEventListener('submit', handler);
      cleanups.push(() => {
        form.removeEventListener('submit', handler);
        statusNode.remove();
      });
    });

    const slides = Array.from(root.querySelectorAll('#carousel-home .item'));
    const indicators = Array.from(root.querySelectorAll('#carousel-home .carousel-indicators li'));
    const prev = root.querySelector('#carousel-home .left.carousel-control');
    const next = root.querySelector('#carousel-home .right.carousel-control');
    const carouselInnerRoot = root.querySelector('#carousel-home .carousel-inner');

    if (carouselInnerRoot) {
      carouselInnerRoot.setAttribute('role', 'region');
      carouselInnerRoot.setAttribute('aria-label', 'Featured solutions slides');
      carouselInnerRoot.removeAttribute('aria-live');
    }

    prev?.setAttribute('aria-label', 'Previous slide');
    next?.setAttribute('aria-label', 'Next slide');

    if (slides.length > 0) {
      let index = Math.max(
        0,
        slides.findIndex((slide) => slide.classList.contains('active')),
      );

      const showSlide = (nextIndex) => {
        index = (nextIndex + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('active', slideIndex === index);
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');
          slide.setAttribute('aria-label', `Slide ${slideIndex + 1} of ${slides.length}`);
          slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
          const video = slide.querySelector('video');
          if (video) {
            video.pause();
            video.removeAttribute('autoplay');
            if (video.currentTime > 0) {
              video.currentTime = 0;
            }
          }
        });

        indicators.forEach((indicator, indicatorIndex) => {
          indicator.classList.toggle('active', indicatorIndex === index);
        });
      };

      const advance = () => showSlide(index + 1);
      const rewind = () => showSlide(index - 1);

      showSlide(index);

      const timer = window.setInterval(advance, 6500);
      cleanups.push(() => window.clearInterval(timer));

      if (prev) {
        const handler = (event) => {
          event.preventDefault();
          rewind();
        };
        prev.addEventListener('click', handler);
        cleanups.push(() => prev.removeEventListener('click', handler));
      }

      if (next) {
        const handler = (event) => {
          event.preventDefault();
          advance();
        };
        next.addEventListener('click', handler);
        cleanups.push(() => next.removeEventListener('click', handler));
      }

      indicators.forEach((indicator, indicatorIndex) => {
        const handler = () => showSlide(indicatorIndex);
        indicator.addEventListener('click', handler);
        cleanups.push(() => indicator.removeEventListener('click', handler));
      });
    }

    const imageCarousels = Array.from(root.querySelectorAll('.vc_images_carousel'));

    imageCarousels.forEach((carousel) => {
      const items = Array.from(carousel.querySelectorAll('.vc_item'));
      const itemWrappers = Array.from(carousel.querySelectorAll('.vc_carousel-slideline-inner > .vc_item'));
      const activeItems = items.length > 0 ? items : itemWrappers;
      const dots = Array.from(carousel.querySelectorAll('.vc_carousel-indicators li'));
      const previousButton = carousel.querySelector('.vc_left.vc_carousel-control');
      const nextButton = carousel.querySelector('.vc_right.vc_carousel-control');

      if (activeItems.length === 0) {
        return;
      }

      let activeIndex = Math.max(
        0,
        activeItems.findIndex((item) => item.classList.contains('is-active') || item.classList.contains('vc_active')),
      );

      const showItem = (nextIndex) => {
        activeIndex = (nextIndex + activeItems.length) % activeItems.length;
        const activeImage = activeItems[activeIndex].querySelector('img');

        activeItems.forEach((item, itemIndex) => {
          const isActive = itemIndex === activeIndex;
          item.classList.toggle('is-active', isActive);
          item.classList.toggle('vc_active', isActive);
          item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('active', dotIndex === activeIndex);
          dot.setAttribute('aria-current', dotIndex === activeIndex ? 'true' : 'false');
        });

        if (activeImage?.getAttribute('src')) {
          carousel.style.backgroundImage = `linear-gradient(rgba(17, 32, 51, 0.16), rgba(17, 32, 51, 0.16)), url("${activeImage.getAttribute('src')}")`;
        }
      };

      showItem(activeIndex);

      if (previousButton) {
        const handler = (event) => {
          event.preventDefault();
          showItem(activeIndex - 1);
        };

        previousButton.addEventListener('click', handler);
        cleanups.push(() => previousButton.removeEventListener('click', handler));
      }

      if (nextButton) {
        const handler = (event) => {
          event.preventDefault();
          showItem(activeIndex + 1);
        };

        nextButton.addEventListener('click', handler);
        cleanups.push(() => nextButton.removeEventListener('click', handler));
      }

      dots.forEach((dot, dotIndex) => {
        const handler = (event) => {
          event.preventDefault();
          showItem(dotIndex);
        };

        dot.addEventListener('click', handler);
        cleanups.push(() => dot.removeEventListener('click', handler));
      });
    });

    const volunteerCarousels = Array.from(root.querySelectorAll('.about-volunteer-carousel'));

    volunteerCarousels.forEach((carousel) => {
      const slides = Array.from(carousel.querySelectorAll('.about-volunteer-slide'));
      const dots = Array.from(carousel.querySelectorAll('.about-volunteer-dot'));
      const previousButton = carousel.querySelector('.about-volunteer-control--prev');
      const nextButton = carousel.querySelector('.about-volunteer-control--next');

      if (slides.length === 0) {
        return;
      }

      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

      const showSlide = (nextIndex) => {
        activeIndex = (nextIndex + slides.length) % slides.length;

        slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === activeIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        dots.forEach((dot, dotIndex) => {
          const isActive = dotIndex === activeIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      };

      showSlide(activeIndex);

      if (previousButton) {
        const handler = (event) => {
          event.preventDefault();
          showSlide(activeIndex - 1);
        };

        previousButton.addEventListener('click', handler);
        cleanups.push(() => previousButton.removeEventListener('click', handler));
      }

      if (nextButton) {
        const handler = (event) => {
          event.preventDefault();
          showSlide(activeIndex + 1);
        };

        nextButton.addEventListener('click', handler);
        cleanups.push(() => nextButton.removeEventListener('click', handler));
      }

      dots.forEach((dot, dotIndex) => {
        const handler = (event) => {
          event.preventDefault();
          showSlide(dotIndex);
        };

        dot.addEventListener('click', handler);
        cleanups.push(() => dot.removeEventListener('click', handler));
      });
    });

    const progressBars = Array.from(root.querySelectorAll('.vc_progress_bar .vc_bar'));
    progressBars.forEach((bar) => {
      const rawValue = bar.getAttribute('data-percentage-value') || bar.getAttribute('data-value') || '0';
      const percentage = Number.parseFloat(rawValue);

      if (!Number.isNaN(percentage)) {
        bar.style.width = `${Math.max(0, Math.min(percentage, 100))}%`;
      }
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [key]);
}

function useShowcaseLightbox(path) {
  useEffect(() => {
    if (!path.startsWith('/digital-signage')) {
      return undefined;
    }

    const root = document.querySelector('.page-surface--solutions');
    if (!root) {
      return undefined;
    }

    const overlay = document.createElement('div');
    overlay.className = 'solutions-lightbox';
    overlay.innerHTML = `
      <button class="solutions-lightbox__close" type="button" aria-label="Close image viewer">Close</button>
      <img class="solutions-lightbox__image" alt="Expanded gallery image" />
    `;

    const image = overlay.querySelector('.solutions-lightbox__image');
    const closeButton = overlay.querySelector('.solutions-lightbox__close');
    const bodyOverflow = document.body.style.overflow;

    const close = () => {
      overlay.classList.remove('is-open');
      document.body.style.overflow = bodyOverflow;
    };

    const open = (src, alt) => {
      image.setAttribute('src', src);
      image.setAttribute('alt', alt || 'Expanded gallery image');
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    const handleRootClick = (event) => {
      const trigger = event.target.closest('.solutions-showcase-gallery__link');
      if (!trigger) {
        return;
      }

      event.preventDefault();
      open(trigger.getAttribute('href') || '', trigger.getAttribute('data-lightbox-alt') || '');
    };

    const handleOverlayClick = (event) => {
      if (event.target === overlay || event.target === closeButton) {
        close();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && overlay.classList.contains('is-open')) {
        close();
      }
    };

    root.addEventListener('click', handleRootClick);
    overlay.addEventListener('click', handleOverlayClick);
    window.addEventListener('keydown', handleEscape);
    document.body.appendChild(overlay);

    return () => {
      root.removeEventListener('click', handleRootClick);
      overlay.removeEventListener('click', handleOverlayClick);
      window.removeEventListener('keydown', handleEscape);
      overlay.remove();
      document.body.style.overflow = bodyOverflow;
    };
  }, [path]);
}

function parsePortfolioEntry(entry) {
  if (typeof DOMParser === 'undefined') {
    return createEmptyPortfolio(entry);
  }

  const parser = new DOMParser();
  const documentRoot = parser.parseFromString(entry.html, 'text/html');
  const title = cleanText(documentRoot.querySelector('#subbanner h1, #home_banner h1, h1')?.textContent) || entry.title;
  const description =
    cleanText(documentRoot.querySelector('#subbanner p, #home_banner p')?.textContent) ||
    entry.excerpt ||
    'Browse selected Creating Margin examples by application type.';

  const filterLinks = Array.from(documentRoot.querySelectorAll('.vp-filter__item a'));
  const baseCategories = filterLinks.map((link) => {
    const slug = link.dataset.vpFilter === '*' ? 'all' : link.dataset.vpFilter;
    return {
      slug,
      label: cleanText(link.textContent) || formatSlug(slug),
    };
  });

  const wraps = Array.from(documentRoot.querySelectorAll('.vp-portfolio__item-wrap'));
  const items = wraps.map((wrap, index) => {
    const imageNode = wrap.querySelector('img');
    const imageLink = wrap.querySelector('.vp-portfolio__item-img a, .vp-portfolio__item-meta');
    const popupTemplate = wrap.querySelector('.vp-portfolio__item-popup');
    const popupContent = popupTemplate?.content || null;
    const rawTitle =
      cleanText(popupContent?.querySelector('.vp-portfolio__item-popup-title')?.textContent) ||
      cleanText(wrap.querySelector('.vp-portfolio__item-meta-title')?.textContent) ||
      `Portfolio Example ${index + 1}`;
    const categories = (wrap.dataset.vpFilter || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    return {
      id: `portfolio-${index + 1}`,
      title: prettifyPortfolioTitle(rawTitle),
      description: cleanText(
        popupContent?.querySelector('.vp-portfolio__item-popup-description')?.textContent,
      ),
      categories,
      image:
        imageNode?.getAttribute('data-src') ||
        imageNode?.getAttribute('src') ||
        imageLink?.getAttribute('href') ||
        '',
      imageSrcSet: extractPreferredImageSrcSet(imageNode),
      fullImage:
        popupTemplate?.getAttribute('data-vp-popup-img') ||
        imageLink?.getAttribute('href') ||
        imageNode?.getAttribute('data-src') ||
        imageNode?.getAttribute('src') ||
        '',
      alt: cleanText(imageNode?.getAttribute('alt')) || prettifyPortfolioTitle(rawTitle),
    };
  });

  const categoryLabels = new Map(baseCategories.map((category) => [category.slug, category.label]));

  items.forEach((item) => {
    item.categories.forEach((category) => {
      if (!categoryLabels.has(category)) {
        categoryLabels.set(category, formatSlug(category));
      }
    });
  });

  const itemCountByCategory = new Map();
  items.forEach((item) => {
    item.categories.forEach((category) => {
      itemCountByCategory.set(category, (itemCountByCategory.get(category) || 0) + 1);
    });
  });

  const categories = [
    { slug: 'all', label: 'All', count: items.length },
    ...Array.from(categoryLabels.entries())
      .filter(([slug]) => slug !== 'all')
      .map(([slug, label]) => ({ slug, label, count: itemCountByCategory.get(slug) || 0 }))
      .filter((category) => category.count > 0),
  ];

  const ctaHref = normalizePath(
    documentRoot.querySelector('#call_of_action .vc_btn3')?.getAttribute('href') || '/contact-us/',
  );

  return {
    title,
    description,
    ctaHref,
    items,
    categories,
    categoryLabels,
  };
}

function createEmptyPortfolio(entry) {
  return {
    title: entry.title,
    description: entry.excerpt || 'Browse selected Creating Margin examples by application type.',
    ctaHref: '/contact-us/',
    items: [],
    categories: [{ slug: 'all', label: 'All', count: 0 }],
    categoryLabels: new Map([['all', 'All']]),
  };
}

function sanitizeSiteData(payload) {
  if (!payload) {
    return payload;
  }

  return {
    ...payload,
    pages: Array.isArray(payload.pages) ? payload.pages.map(sanitizeEntryContent) : payload.pages,
    posts: Array.isArray(payload.posts) ? payload.posts.map(sanitizeEntryContent) : payload.posts,
  };
}

function sanitizeEntryContent(entry) {
  if (!entry?.html) {
    return entry;
  }

  const html = entry.html.replace(/https?:\/\/www\.yogitots\.com\/contact-us\/?/gi, '/contact-us/');
  const normalizedHtml = entry.kind === 'post' ? sanitizeLegacyPostHtml(html) : html;

  if (typeof DOMParser === 'undefined') {
    return {
      ...entry,
      html: normalizedHtml,
    };
  }

  const parser = new DOMParser();
  const documentRoot = parser.parseFromString(normalizedHtml, 'text/html');

  normalizeMigratedLinks(documentRoot);
  normalizeMigratedResponsiveImages(documentRoot);
  normalizeMigratedEmbeds(documentRoot);
  normalizeLegacyInlineActions(documentRoot);
  normalizePostContent(documentRoot, entry);

  return {
    ...entry,
    html: documentRoot.body.innerHTML,
  };
}

function normalizePostContent(documentRoot, entry) {
  if (entry?.kind !== 'post') {
    return;
  }

  const postRoot = documentRoot.querySelector('article.post, article.type-post');
  const scopeRoot = postRoot || documentRoot.body;

  Array.from(scopeRoot.querySelectorAll('.buttons, .ovrlyT, .ovrlyB, .author-wrap, .entry-header, .navigation.post-navigation, .post-navigation, .nav-links, .screen-reader-text')).forEach((node) => {
    node.remove();
  });

  Array.from(scopeRoot.querySelectorAll('a.fa-google, a.fa-facebook, a.fa-twitter')).forEach((node) => {
    node.remove();
  });

  const mediaImage = scopeRoot.querySelector('.entry-media img');
  if (mediaImage && !cleanText(mediaImage.getAttribute('alt'))) {
    mediaImage.setAttribute('alt', cleanText(entry?.title) || 'Creating Margin article image');
  }

  const mediaContainer = scopeRoot.querySelector('.entry-media');
  const contentWrapper = scopeRoot.querySelector('.post-content-wrapper');

  if (mediaContainer && contentWrapper) {
    const heroFigure = documentRoot.createElement('figure');
    heroFigure.className = 'post-lead-media';

    const leadImage = mediaContainer.querySelector('img');
    if (leadImage) {
      heroFigure.appendChild(leadImage.cloneNode(true));
    }

    if (heroFigure.children.length > 0) {
      contentWrapper.prepend(heroFigure);
    }

    mediaContainer.remove();
  }

  Array.from(scopeRoot.querySelectorAll('p')).forEach((paragraph) => {
    if (!cleanText(paragraph.textContent) && paragraph.children.length === 0) {
      paragraph.remove();
    }
  });

  // Remove empty list markers often left by malformed migrated markup.
  Array.from(scopeRoot.querySelectorAll('li')).forEach((item) => {
    if (!cleanText(item.textContent) && !item.querySelector('img,video,iframe,a,svg')) {
      item.remove();
    }
  });

  Array.from(scopeRoot.querySelectorAll('ul, ol')).forEach((list) => {
    const hasContent = Array.from(list.children).some((item) => cleanText(item.textContent));
    if (!hasContent) {
      list.remove();
    }
  });

  // Remove legacy author bio/footer block and post-navigation headings when present.
  Array.from(scopeRoot.querySelectorAll('h1, h2, h3, h4, h5, h6, p, strong')).forEach((node) => {
    const label = cleanText(node.textContent).toLowerCase();

    if (!label) {
      return;
    }

    if (label.startsWith('author by')) {
      let cursor = node;
      while (cursor) {
        const next = cursor.nextElementSibling;
        const nextLabel = cleanText(next?.textContent || '').toLowerCase();

        cursor.remove();

        if (!next) {
          break;
        }

        if (/^post navigation$/.test(nextLabel)) {
          cursor = next;
          continue;
        }

        if (/^(h1|h2|h3|h4|h5|h6)$/i.test(next.tagName) && !nextLabel.startsWith('author by')) {
          break;
        }

        cursor = next;
      }
    }
  });

  Array.from(scopeRoot.querySelectorAll('h1, h2, h3, h4, h5, h6')).forEach((heading) => {
    if (cleanText(heading.textContent).toLowerCase() === 'post navigation') {
      const sibling = heading.nextElementSibling;
      heading.remove();
      if (sibling && sibling.querySelector('a')) {
        sibling.remove();
      }
    }
  });

  // Some exported posts contain author boilerplate as a plain paragraph; remove those by signature text.
  Array.from(scopeRoot.querySelectorAll('p, div')).forEach((node) => {
    const label = cleanText(node.textContent).toLowerCase();
    if (!label) {
      return;
    }

    if (label.includes('author by') || label.includes('freshclothes.app') || label.includes('franchise opportunities')) {
      const containsPostBody = node.querySelector('h1, h2, h3, h4, h5, h6, article, section');
      if (!containsPostBody) {
        node.remove();
      }
    }
  });
}

function normalizeMigratedLinks(documentRoot) {
  Array.from(documentRoot.querySelectorAll('a[href]')).forEach((link) => {
    const href = (link.getAttribute('href') || '').trim();
    const label = cleanText(link.textContent).toLowerCase();
    const title = cleanText(link.getAttribute('title')).toLowerCase();

    if ((href === '#' || href === '') && (label === 'contact us' || title === 'contact us')) {
      link.setAttribute('href', '/contact-us/');
    }
  });

  Array.from(documentRoot.querySelectorAll('#hostikolayout22-tlds > .wpb_column')).forEach((card) => {
    const imageLink = card.querySelector('.wpb_single_image a[href]');
    const textLink = card.querySelector('.searchdomain-tldtxt a[href]');
    const wrapper = card.querySelector(':scope > .vc_column-inner > .wpb_wrapper');

    if (imageLink && textLink && imageLink.getAttribute('href') !== textLink.getAttribute('href')) {
      imageLink.setAttribute('href', textLink.getAttribute('href'));
    }

    const href = textLink?.getAttribute('href') || imageLink?.getAttribute('href');

    if (!wrapper || !href || wrapper.querySelector(':scope > .service-card-link')) {
      return;
    }

    const unifiedLink = documentRoot.createElement('a');
    unifiedLink.className = 'service-card-link';
    unifiedLink.setAttribute('href', href);

    const imageBlock = card.querySelector('.wpb_single_image');
    const textBlock = card.querySelector('.searchdomain-tldtxt');

    if (imageBlock) {
      const figureWrapper = imageBlock.querySelector('.vc_single_image-wrapper');
      const image = imageBlock.querySelector('img');

      if (figureWrapper && image) {
        const iconShell = documentRoot.createElement('span');
        iconShell.className = 'service-card-link__icon';
        const clonedImage = image.cloneNode(true);
        clonedImage.removeAttribute('srcset');
        clonedImage.removeAttribute('sizes');
        iconShell.appendChild(clonedImage);
        unifiedLink.appendChild(iconShell);
      }
    }

    if (textBlock) {
      const labelText = cleanText(textBlock.textContent);

      if (labelText) {
        const label = documentRoot.createElement('span');
        label.className = 'service-card-link__label';
        label.textContent = labelText;
        unifiedLink.appendChild(label);
      }
    }

    if (unifiedLink.children.length === 0) {
      return;
    }

    wrapper.replaceChildren(unifiedLink);
  });
}

function normalizeMigratedResponsiveImages(documentRoot) {
  Array.from(documentRoot.querySelectorAll('img')).forEach((image) => {
    const source = (image.getAttribute('src') || '').trim();
    const dataSource = (image.getAttribute('data-src') || '').trim();
    const dataSrcset = (image.getAttribute('data-srcset') || '').trim();
    const currentSrcset = (image.getAttribute('srcset') || '').trim();
    const isPlaceholderGif = source.startsWith('data:image/gif;base64');
    const isPlaceholderSrcset = currentSrcset.toLowerCase().startsWith('data:image/');

    // WordPress lazyload markup frequently ships real URLs in data-src/data-srcset.
    // Promote them to standard attributes so images render without WP lazyload JS.
    if (dataSource && (isPlaceholderGif || !source)) {
      image.setAttribute('src', dataSource);
    }

    if (dataSrcset && (!currentSrcset || isPlaceholderSrcset)) {
      image.setAttribute('srcset', dataSrcset);
    }

    const effectiveSource = (image.getAttribute('src') || '').trim();
    const effectiveSrcset = (image.getAttribute('srcset') || '').trim();
    const shouldNormalize =
      effectiveSource.startsWith('/wp-content/uploads/') ||
      effectiveSrcset.includes('/wp-content/uploads/');

    const inferredDimensions = inferImageDimensionsFromUrl(effectiveSource || dataSource || source);
    if (inferredDimensions?.width && inferredDimensions?.height) {
      if (!image.getAttribute('width')) {
        image.setAttribute('width', String(inferredDimensions.width));
      }

      if (!image.getAttribute('height')) {
        image.setAttribute('height', String(inferredDimensions.height));
      }
    }

    if (shouldNormalize) {
      image.removeAttribute('sizes');
    }

    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');

    const className = image.getAttribute('class') || '';
    if (className.includes('lazyload') || className.includes('lazyloading') || className.includes('lazyloaded')) {
      const nextClassName = className
        .split(/\s+/)
        .filter((token) => token && token !== 'lazyload' && token !== 'lazyloading' && token !== 'lazyloaded')
        .join(' ')
        .trim();

      if (nextClassName) {
        image.setAttribute('class', nextClassName);
      } else {
        image.removeAttribute('class');
      }
    }
  });
}

function normalizeMigratedEmbeds(documentRoot) {
  Array.from(documentRoot.querySelectorAll('iframe')).forEach((iframe) => {
    const source = (iframe.getAttribute('src') || '').trim();
    const dataSource = (iframe.getAttribute('data-src') || '').trim();
    const isPlaceholderGif = source.startsWith('data:image/gif;base64');

    // Promote lazy embed URLs so maps/videos render without WP lazyload JavaScript.
    if (dataSource && (isPlaceholderGif || !source)) {
      iframe.setAttribute('src', dataSource);
    }

    iframe.removeAttribute('data-src');

    const className = iframe.getAttribute('class') || '';
    if (className.includes('lazyload') || className.includes('lazyloading') || className.includes('lazyloaded')) {
      const nextClassName = className
        .split(/\s+/)
        .filter((token) => token && token !== 'lazyload' && token !== 'lazyloading' && token !== 'lazyloaded')
        .join(' ')
        .trim();

      if (nextClassName) {
        iframe.setAttribute('class', nextClassName);
      } else {
        iframe.removeAttribute('class');
      }
    }
  });
}

function normalizeLegacyInlineActions(documentRoot) {
  Array.from(documentRoot.querySelectorAll('[onclick]')).forEach((element) => {
    const legacyAction = parseLegacyInlineAction(element.getAttribute('onclick'));

    if (!legacyAction) {
      return;
    }

    element.removeAttribute('onclick');
    element.setAttribute('data-legacy-action', legacyAction.type);
    element.setAttribute('data-legacy-targets', legacyAction.targets.join('|'));

    if (element.tagName === 'BUTTON' && !element.getAttribute('type')) {
      element.setAttribute('type', 'button');
    }
  });
}

function parseLegacyInlineAction(value) {
  const expression = cleanText(value);
  const match = expression.match(/^(showElements|hideElements)\s*\(\s*\[(.*)\]\s*\)$/);

  if (!match) {
    return null;
  }

  const targets = [...match[2].matchAll(/['"]([^'"]+)['"]/g)].map((entry) => entry[1]);

  if (targets.length === 0) {
    return null;
  }

  return {
    type: match[1] === 'showElements' ? 'show-elements' : 'hide-elements',
    targets,
  };
}

function normalizeLegacyTargets(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => cleanText(entry)).filter(Boolean);
  }

  return String(value || '')
    .split('|')
    .map((entry) => cleanText(entry))
    .filter(Boolean);
}

function transformHomeMarkup(html) {
  if (!html) {
    return html;
  }

  let output = html;

  // Use DOM to reliably remove the "Stunning Content" carousel slide and its indicator
  if (typeof DOMParser !== 'undefined') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(output, 'text/html');

    // Remove the third indicator (data-slide-to="2")
    const thirdIndicator = doc.querySelector('#carousel-home .carousel-indicators li[data-slide-to="2"]');
    thirdIndicator?.remove();

    // Remove the carousel item that uses the rowers-banner background (the Stunning Content slide)
    // and any slide whose heading is explicitly "Stunning Content".
    const carouselItems = Array.from(doc.querySelectorAll('#carousel-home .item'));
    carouselItems.forEach((item) => {
      const dataBgImage = cleanText(item.getAttribute('data-bg-image'));
      if (dataBgImage) {
        item.style.backgroundImage = dataBgImage;
      }

      const bg = item.getAttribute('style') || '';
      const headingText = cleanText(item.querySelector('h1, h2, h3, h4')?.textContent).toLowerCase();

      if (bg.includes('rowers-banner') || headingText === 'stunning content') {
        item.remove();
      }
    });

    const carouselInner = doc.querySelector('#carousel-home .carousel-inner');
    const carouselIndicators = doc.querySelector('#carousel-home .carousel-indicators');
    const carouselRoot = doc.querySelector('#carousel-home');
    const carouselPrev = doc.querySelector('#carousel-home .left.carousel-control');
    const carouselNext = doc.querySelector('#carousel-home .right.carousel-control');

    if (carouselRoot) {
      carouselRoot.setAttribute('aria-label', 'Featured solutions carousel');
      carouselRoot.setAttribute('aria-roledescription', 'carousel');
    }

    if (carouselInner) {
      // The legacy WP markup uses role=listbox but child slides are not option elements.
      // Use region semantics to avoid invalid role-child relationships in audits.
      carouselInner.setAttribute('role', 'region');
      carouselInner.setAttribute('aria-label', 'Featured solutions slides');
      carouselInner.removeAttribute('aria-live');
    }

    if (carouselPrev) {
      carouselPrev.setAttribute('aria-label', 'Previous slide');
      carouselPrev.setAttribute('title', 'Previous slide');
    }

    if (carouselNext) {
      carouselNext.setAttribute('aria-label', 'Next slide');
      carouselNext.setAttribute('title', 'Next slide');
    }

    if (carouselInner && carouselIndicators) {
      const existingSlideKeys = new Set(
        Array.from(carouselInner.querySelectorAll('.item h2')).map((heading) => cleanText(heading.textContent).toLowerCase()),
      );

      const additionalSlides = [
        {
          eyebrow: 'Practical AI Services',
          heading: 'AI Consulting',
          description:
            'Apply Generative AI to real business workflows across operations, content production, and digital signage execution.',
          href: '/contact-us/',
          cta: 'Inquire Now',
          imageUrl: '/wp-content/uploads/2020/02/ProfessionalServicesHeader_813x647v2.png',
        },
        {
          eyebrow: 'Tailored Experiences',
          heading: 'Interactive Digital Signage',
          description: 'Design engaging touch experiences that guide visitors and improve self-service workflows.',
          href: '/digital-signage/interactive-digital-signage/',
          cta: 'Explore Solutions',
          imageUrl: '/wp-content/uploads/2020/04/Hyatt_Albuquerque_Digital_Signage_Wall_Mounted_Portrait.jpg',
        },
        {
          eyebrow: 'Proven Results',
          heading: 'See Our Portfolio',
          description: 'Browse real-world deployments across industries and discover what is possible for your space.',
          href: '/portfolio/',
          cta: 'View Portfolio',
          imageUrl: '/wp-content/uploads/2020/01/Liberty_Global_Bank_Electronic_Signage_Denver_2.jpg',
        },
        {
          eyebrow: 'Smarter Workplaces',
          heading: 'Workplace Management',
          description: 'Coordinate spaces, rooms, and visitors with workplace tools that keep teams informed.',
          href: '/workplace-management/',
          cta: 'Explore Workplace',
          imageUrl: '/wp-content/uploads/2020/04/Facility_Management_Digital_Signage_Workplace_Management.jpg',
        },
      ];

      additionalSlides.forEach((slide) => {
        if (existingSlideKeys.has(slide.heading.toLowerCase())) {
          return;
        }

        const item = doc.createElement('div');
        item.className = 'item';
        item.style.backgroundImage = `url('${slide.imageUrl}')`;

        const caption = doc.createElement('div');
        caption.className = 'carousel-caption';

        const eyebrow = doc.createElement('h3');
        eyebrow.textContent = slide.eyebrow;

        const heading = doc.createElement('h2');
        heading.textContent = slide.heading;

        const description = doc.createElement('p');
        description.textContent = slide.description;

        const cta = doc.createElement('a');
        if (slide.href && slide.cta) {
          cta.setAttribute('href', slide.href);
          cta.textContent = slide.cta;
        }

        caption.append(eyebrow, heading, description);
        if (slide.href && slide.cta) {
          caption.appendChild(cta);
        }
        item.appendChild(caption);
        carouselInner.appendChild(item);

        const indicator = doc.createElement('li');
        indicator.setAttribute('data-target', '#carousel-home');
        carouselIndicators.appendChild(indicator);
      });

      // Keep the rowers hero in the second-to-last slot.
      const slidesAfterInsert = Array.from(carouselInner.querySelectorAll('.item'));
      const rowingSlide = slidesAfterInsert.find((item) => {
        const heading = cleanText(item.querySelector('h2')?.textContent).toLowerCase();
        const style = item.getAttribute('style') || '';
        return heading === 'effective solutions' || style.includes('rowers2');
      });
      const lastSlide = slidesAfterInsert[slidesAfterInsert.length - 1];

      if (rowingSlide && lastSlide && rowingSlide !== lastSlide) {
        carouselInner.insertBefore(rowingSlide, lastSlide);
      }

      // Keep indicators and active slide state in sync after slide updates.
      const indicators = Array.from(carouselIndicators.querySelectorAll('li'));
      indicators.forEach((indicator, index) => {
        indicator.setAttribute('data-slide-to', String(index));
        indicator.classList.toggle('active', index === 0);
      });

      const visibleItems = Array.from(carouselInner.querySelectorAll('.item'));
      visibleItems.forEach((item, index) => {
        item.classList.toggle('active', index === 0);
        item.setAttribute('role', 'group');
        item.setAttribute('aria-roledescription', 'slide');
        item.setAttribute('aria-label', `Slide ${index + 1} of ${visibleItems.length}`);
        item.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      });
    }

    const socialDistancingHeading = Array.from(doc.querySelectorAll('h1, h2, h3, h4')).find(
      (heading) => heading.textContent?.trim() === 'Social Distancing & Workplace Safety Solutions',
    );
    socialDistancingHeading?.remove();

    const effectiveSolutionsHeading = Array.from(doc.querySelectorAll('h1, h2, h3, h4')).find(
      (heading) => heading.textContent?.trim() === 'Effective Solutions for Real Issues',
    );
    if (effectiveSolutionsHeading) {
      effectiveSolutionsHeading.textContent = 'Featured Workplace Solutions';
    }

    const portfolioPrideHeading = Array.from(doc.querySelectorAll('h1, h2, h3, h4')).find(
      (heading) => heading.textContent?.trim() === 'We Take Pride In Who We Work With',
    );
    const portfolioPrideRow = portfolioPrideHeading?.closest('.vc_row');

    if (portfolioPrideRow) {
      portfolioPrideRow.classList.add('home-portfolio-pride-section');

      const portfolioPrideColumns = Array.from(portfolioPrideRow.children).filter((node) =>
        node.classList?.contains('wpb_column'),
      );

      portfolioPrideColumns.forEach((column) => {
        if (column.classList.contains('vc_col-sm-2')) {
          column.classList.add('home-portfolio-pride-section__spacer');
          return;
        }

        if (column.classList.contains('vc_col-sm-3')) {
          column.classList.add('home-portfolio-pride-section__media');
          return;
        }

        if (column.classList.contains('vc_col-sm-7')) {
          column.classList.add('home-portfolio-pride-section__content');
        }
      });
    }

    output = doc.body.innerHTML;
  }

  output = output.replace(
    '</div></div></div></div><div class="vc_row-full-width vc_clearfix"></div><div id="home_features"',
    '</div></div></div></div><div class="vc_row-full-width vc_clearfix"></div><div id="home_features"',
  );

  return output;
}

function transformMigratedMarkup(entry) {
  if (!entry?.html) {
    return entry?.html || '';
  }

  const output = entry.html;

  if (typeof DOMParser === 'undefined') {
    return output;
  }

  const parser = new DOMParser();
  const documentRoot = parser.parseFromString(output, 'text/html');

  normalizeMigratedLinks(documentRoot);

  pruneEmptyMigratedRows(documentRoot);

  if (entry.path === '/about-us') {
    transformAboutPage(documentRoot);
  }

  if (entry.path.startsWith('/contact-us')) {
    transformContactPage(documentRoot);
  }

  if (
    entry.path.startsWith('/digital-signage') ||
    entry.path.startsWith('/workplace-management') ||
    entry.path.startsWith('/contivision') ||
    entry.path.startsWith('/professional-services')
  ) {
    transformSolutionsPage(documentRoot);
  }

  // Tag any fluent forms not inside #contact_form so they pick up shared card styles
  Array.from(documentRoot.querySelectorAll('.frm-fluent-form')).forEach((form) => {
    if (!form.closest('#contact_form')) {
      form.classList.add('contact-form-card');
    }
  });

  return documentRoot.body.innerHTML;
}

function transformContactPage(documentRoot) {
  const contactFormRow = documentRoot.querySelector('#contact_form');
  Array.from(contactFormRow?.querySelectorAll('.frm-fluent-form') || []).forEach((form) => {
    form.classList.add('contact-form-card');
  });

  normalizeContactFormColumns(contactFormRow, documentRoot);

  const calloutHeading = Array.from(documentRoot.querySelectorAll('h1, h2, h3, h4, h5, h6')).find((heading) =>
    /^or$/i.test(cleanText(heading.textContent)),
  );
  const calloutSection = calloutHeading?.closest('.vc_row');
  calloutSection?.classList.add('contact-callout-section');

  const calloutPhoneLink = Array.from(calloutSection?.querySelectorAll('a[href]') || []).find((link) =>
    (link.getAttribute('href') || '').startsWith('tel:'),
  );
  calloutPhoneLink?.classList.add('contact-callout-phone');
  const calloutPhoneWrap = calloutPhoneLink?.closest('p');
  calloutPhoneWrap?.classList.add('contact-callout-phone-wrap');

  if (calloutPhoneLink?.textContent) {
    calloutPhoneLink.textContent = calloutPhoneLink.textContent.replace(/\s*@\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
  }

  Array.from(documentRoot.querySelectorAll('h1, h2, h3, h4, h5, h6')).forEach((heading) => {
    const text = cleanText(heading.textContent);

    if (/^for sales enquiries$/i.test(text)) {
      heading.textContent = 'For Sales Inquiries';
    }
  });

  Array.from(documentRoot.querySelectorAll('p')).forEach((paragraph) => {
    const text = paragraph.textContent || '';

    if (/sales enquiry/i.test(text)) {
      paragraph.textContent = text.replace(/sales enquiry/gi, 'sales inquiry');
    }
  });

  const faqSection = Array.from(documentRoot.querySelectorAll('.vc_row')).find((row) =>
    /frequently asked questions/i.test(cleanText(row.textContent)),
  );
  const faqTabs = faqSection?.querySelector('.vc_tta');

  if (faqTabs) {
    const brightSignLink = Array.from(faqTabs.querySelectorAll('.vc_tta-tab a')).find((link) =>
      /^brightsign$/i.test(cleanText(link.textContent)),
    );

    const brightSignTarget = brightSignLink?.getAttribute('href') || '';
    const brightSignPanelId = brightSignTarget.startsWith('#') ? brightSignTarget.slice(1) : '';

    brightSignLink?.closest('.vc_tta-tab')?.remove();

    if (brightSignPanelId) {
      const brightSignPanel = Array.from(faqTabs.querySelectorAll('.vc_tta-panel')).find(
        (panel) => panel.id === brightSignPanelId,
      );
      brightSignPanel?.remove();
    }
  }
}

function normalizeContactFormColumns(contactFormRow, documentRoot) {
  if (!contactFormRow) {
    return;
  }

  const contentWrapper = contactFormRow.querySelector(':scope > .wpb_column > .vc_column-inner > .wpb_wrapper');
  if (!contentWrapper) {
    return;
  }

  const forms = Array.from(contentWrapper.querySelectorAll('.frm-fluent-form'));
  if (forms.length < 2) {
    return;
  }

  const supportHeading = Array.from(contentWrapper.children).find((node) =>
    /^support and managed services$/i.test(cleanText(node.textContent)),
  );
  const salesHeading = Array.from(contentWrapper.children).find((node) =>
    /^for sales enqu?iries$/i.test(cleanText(node.textContent)),
  );

  const supportFormBlock = forms[0].closest('.wpb_raw_html, .wpb_text_column, .wpb_raw_code');
  const salesFormBlock = forms[1].closest('.wpb_raw_html, .wpb_text_column, .wpb_raw_code');

  if (!supportHeading || !salesHeading || !supportFormBlock || !salesFormBlock) {
    return;
  }

  const supportIntro = findNextParagraphSibling(supportHeading, salesHeading);
  const salesIntro = findNextParagraphSibling(salesHeading, salesFormBlock);

  const columns = documentRoot.createElement('div');
  columns.className = 'contact-form-columns';

  const supportColumn = documentRoot.createElement('section');
  supportColumn.className = 'contact-form-column contact-form-column--support';

  const salesColumn = documentRoot.createElement('section');
  salesColumn.className = 'contact-form-column contact-form-column--sales';

  supportColumn.appendChild(supportHeading);
  if (supportIntro) {
    supportColumn.appendChild(supportIntro);
  }
  supportColumn.appendChild(supportFormBlock);

  salesColumn.appendChild(salesHeading);
  if (salesIntro) {
    salesColumn.appendChild(salesIntro);
  }
  salesColumn.appendChild(salesFormBlock);

  columns.appendChild(supportColumn);
  columns.appendChild(salesColumn);

  contentWrapper.appendChild(columns);

  Array.from(contentWrapper.querySelectorAll(':scope > .wpb_raw_code')).forEach((block) => {
    if (block.querySelector('.frm-fluent-form')) {
      return;
    }

    if (cleanText(block.textContent)) {
      return;
    }

    block.remove();
  });
}

function findNextParagraphSibling(startNode, stopNode) {
  let current = startNode?.nextElementSibling || null;

  while (current && current !== stopNode) {
    if (current.matches('p, .wpb_text_column')) {
      return current;
    }

    current = current.nextElementSibling;
  }

  return null;
}

function transformSolutionsPage(documentRoot) {
  tagClientLogoSections(documentRoot);

  const digitalSignageShowcaseRow = documentRoot.querySelector('.vc_row.vc_custom_1581548144846');
  if (digitalSignageShowcaseRow) {
    digitalSignageShowcaseRow.classList.add('solutions-showcase-section');

    const showcaseColumns = Array.from(digitalSignageShowcaseRow.children).filter(
      (column) =>
        column.classList?.contains('wpb_column') &&
        column.classList.contains('vc_column_container') &&
        column.classList.contains('vc_col-sm-4'),
    );
    const applicationsColumn = showcaseColumns[0];
    const mediaColumn = showcaseColumns.find((column) => column.classList.contains('vc_hidden-xs')) || showcaseColumns[1];
    const hardwareColumn = showcaseColumns[showcaseColumns.length - 1];

    applicationsColumn?.classList.add('solutions-showcase-col--applications');
    mediaColumn?.classList.add('solutions-showcase-col--media');
    hardwareColumn?.classList.add('solutions-showcase-col--hardware');

    Array.from(digitalSignageShowcaseRow.querySelectorAll('.features_box > .wpb_column')).forEach((column) => {
      const hasIcon = !!column.querySelector('img');
      const hasCopy = !!column.querySelector('h4, p, a');

      if (hasIcon) {
        column.classList.add('solutions-showcase-feature__icon-col');
      }

      if (hasCopy) {
        column.classList.add('solutions-showcase-feature__copy-col');
      }
    });

    const mediaGrid = mediaColumn?.querySelector('.vc_grid-container');
    const mediaWrapper = mediaColumn?.querySelector('.vc_grid-container-wrapper, .wpb_wrapper');
    const galleryHasContent = !!(mediaGrid && mediaGrid.children.length > 0);

    if (mediaWrapper && !galleryHasContent) {
      mediaWrapper.innerHTML = createDigitalSignageShowcaseGalleryMarkup();
    }
  }

  const introSection = Array.from(documentRoot.querySelectorAll('.entry-content-vc > .vc_row, .entry-content-vc > .vc_row[data-vc-full-width="true"]')).find(
    (row) => {
      if (row.classList.contains('subbanner_hostiko22')) {
        return false;
      }

      if (row.id === 'call_of_action') {
        return false;
      }

      return !!row.querySelector('h2');
    },
  );

  introSection?.classList.add('solutions-intro-section');

  const workplaceOverviewSection = Array.from(documentRoot.querySelectorAll('#home_features.vc_row, #home_features')).find(
    (row) => /workplace management solutions/i.test(cleanText(row.textContent)),
  );

  if (workplaceOverviewSection) {
    workplaceOverviewSection.classList.add('solutions-workplace-overview');

    const rowColumns = Array.from(workplaceOverviewSection.children).filter(
      (column) => column.classList?.contains('wpb_column'),
    );
    const mediaColumn = rowColumns.find((column) => column.classList.contains('hostiko_features_img'));
    const spacerColumn = rowColumns.find((column) => column.classList.contains('vc_col-sm-1'));
    const contentColumn = rowColumns.find((column) => column.classList.contains('vc_col-sm-6'));

    mediaColumn?.classList.add('solutions-workplace-overview__media');
    spacerColumn?.classList.add('solutions-workplace-overview__spacer');
    contentColumn?.classList.add('solutions-workplace-overview__content');

    Array.from(workplaceOverviewSection.querySelectorAll('.features_box')).forEach((row) => {
      row.classList.add('solutions-workplace-overview__item');

      const itemColumns = Array.from(row.children).filter((column) => column.classList?.contains('wpb_column'));
      const iconColumn = itemColumns.find((column) => column.classList.contains('vc_col-sm-3'));
      const copyColumn = itemColumns.find((column) => column.classList.contains('vc_col-sm-9'));

      iconColumn?.classList.add('solutions-workplace-overview__icon-col');
      copyColumn?.classList.add('solutions-workplace-overview__copy-col');
    });
  }

  // Professional Services main grid transform
  // Restore: only run grid transform for /professional-services root, and only inside .entry-content-vc
  if (documentRoot.querySelector('body > .wpb_wrapper > h1')?.textContent?.includes('Professional Services')) {
    const mainWrapper = documentRoot.querySelector('body > .wpb_wrapper');
    const columns = Array.from(documentRoot.querySelectorAll('.vc_column_container, .wpb_column'));
    const serviceCards = columns
      .map((col) => {
        const heading = cleanText(col.querySelector('h4')?.textContent);
        if (!heading) return null;
        const icon = col.querySelector('img');
        const iconSrc = icon?.getAttribute('src') || '';
        const iconAlt = cleanText(icon?.getAttribute('alt')) || heading;
        const body = cleanText(col.querySelector('p')?.textContent);
        const link = col.querySelector('a[href]');
        const href = link?.getAttribute('href') || '#';
        return { heading, iconSrc, iconAlt, body, href };
      })
      .filter(Boolean);

    if (serviceCards.length > 0 && mainWrapper) {
      mainWrapper.insertAdjacentHTML('afterend', createProfessionalServicesGridMarkup(serviceCards));
      // Remove the old columns
      columns.forEach((col) => col.remove());
    }
  }
  function createProfessionalServicesGridMarkup(cards) {
    const items = cards
      .map(
        (card) => `
          <article class="professional-services-card">
            ${card.iconSrc ? `<div class="professional-services-card__icon"><img src="${escapeHtmlAttribute(card.iconSrc)}" alt="${escapeHtmlAttribute(card.iconAlt)}" loading="lazy" /></div>` : ''}
            <h4 class="professional-services-card__heading">${escapeHtmlAttribute(card.heading)}</h4>
            ${card.body ? `<p class="professional-services-card__body">${escapeHtmlAttribute(card.body)}</p>` : ''}
            <a class="professional-services-card__link" href="${escapeHtmlAttribute(card.href)}">Learn More</a>
          </article>`,
      )
      .join('');

    return `
      <section class="professional-services-grid" aria-label="Professional services">
        ${items}
      </section>`;
  }
  // Fix for 'Other Services Offered by Creating Margin' VC hover/duplicate figures
  Array.from(documentRoot.querySelectorAll('.vc_row#gurrente_inner')).forEach((row) => {
    Array.from(row.querySelectorAll('.wpb_single_image')).forEach((imgBlock, idx) => {
      // Only keep the first .wpb_single_image per card (normal state)
      if (imgBlock.classList.contains('gurent_hover')) {
        imgBlock.remove();
      }
    });
  });

  // Tag Supported Brands section and each brand row as cards
  const brandRows = Array.from(documentRoot.querySelectorAll('.vc_row#gurrente_inner')).filter((row) =>
    Array.from(row.querySelectorAll('img')).some((image) => /logo/i.test(cleanText(image.getAttribute('alt')))),
  );
  if (brandRows.length > 0) {
    const brandSection = brandRows[0].closest('#hostiko_gurrentee_sec, .vc_row');
    brandSection?.classList.add('solutions-brands-section');
    brandRows.forEach((row) => {
      row.classList.add('solutions-brand-card');
    });
  }

  const pillarsHeading = Array.from(documentRoot.querySelectorAll('.vc_row h2, .vc_row h3')).find(
    (heading) => cleanText(heading.textContent).toLowerCase() === 'four pillars of our success',
  );
  const pillarsSection = pillarsHeading?.closest('.vc_row');

  if (pillarsSection) {
    pillarsSection.classList.add('solutions-pillars-section');

    const pillarsGrid = Array.from(pillarsSection.querySelectorAll('.vc_row#gurrente_inner')).find((row) =>
      /effective results|reliable services|original content|clear guidance/i.test(cleanText(row.textContent)),
    );

    pillarsGrid?.classList.add('solutions-pillars-grid');
  }

  const wayfindingTypesHeading = Array.from(documentRoot.querySelectorAll('.vc_row h2, .vc_row h3')).find(
    (heading) => cleanText(heading.textContent).toLowerCase() === 'types of wayfinding',
  );
  const wayfindingTypesSection = wayfindingTypesHeading?.closest('.vc_row');

  if (wayfindingTypesSection) {
    wayfindingTypesSection.classList.add('solutions-wayfinding-types-section');
    Array.from(wayfindingTypesSection.querySelectorAll('.vc_tta')).forEach((tabRoot) => {
      tabRoot.classList.add('solutions-wayfinding-tabs');
    });
  }
  const pricingRows = Array.from(documentRoot.querySelectorAll('.vc_row')).filter(
    (row) => row.querySelector('.subplans_sec'),
  );

  pricingRows.forEach((row) => {
    const plans = Array.from(row.querySelectorAll('.subplans_sec')).reduce((items, card) => {
      const title = cleanText(card.querySelector('h3')?.textContent);
      const badge = cleanText(card.querySelector('.plans_label')?.textContent);
      const price = cleanText(card.querySelector('.plans_content_sec h2')?.textContent);
      const button = card.querySelector('a[href]');
      const href = button?.getAttribute('href') || '/contact-us/';
      const label = cleanText(button?.textContent) || 'Contact us';
      const pricingCopy = Array.from(card.querySelectorAll('.plan_pricing_box p')).map((node) => cleanText(node.textContent)).filter(Boolean);
      const details = Array.from(card.querySelectorAll('.plans_content_outer')).reduce((entries, block) => {
        const entryTitle = cleanText(block.querySelector('.plans_content_sec h3')?.textContent);
        const entryBody = cleanText(block.querySelector('.plans_content_sec p')?.textContent);
        const icon = block.querySelector('img');

        if (!entryTitle && !entryBody) {
          return entries;
        }

        entries.push({
          title: entryTitle,
          body: entryBody,
          iconSrc: icon?.getAttribute('src') || '',
          iconAlt: cleanText(icon?.getAttribute('alt')) || entryTitle || title,
        });

        return entries;
      }, []);

      if (!title) {
        return items;
      }

      items.push({
        title,
        badge,
        price,
        pricingCopy,
        details,
        href,
        label,
      });

      return items;
    }, []);

    if (plans.length > 0) {
      row.outerHTML = createProfessionalPlansGridMarkup(plans);
    }
  });

  const featureRows = Array.from(documentRoot.querySelectorAll('.features_box, .subfeatures_row_cont')).filter(
    (row) => row.querySelector('.subfeatures_inner_col'),
  );

  featureRows.forEach((row) => {
    const cards = Array.from(row.querySelectorAll('.subfeatures_inner_col')).reduce((items, card) => {
      const icon = card.querySelector('img');
      const title = cleanText(card.querySelector('h4')?.textContent);
      const body = cleanText(card.querySelector('p')?.textContent);

      if (!title || !body) {
        return items;
      }

      items.push({
        iconSrc: icon?.getAttribute('src') || '',
        iconAlt: cleanText(icon?.getAttribute('alt')) || title,
        title,
        body,
      });

      return items;
    }, []);

    if (cards.length > 0) {
      row.outerHTML = createSolutionsFeatureGridMarkup(cards);
    }
  });

  const applicationRows = Array.from(documentRoot.querySelectorAll('.vc_row')).filter(
    (row) => row.querySelector('.solutions-feature-grid') && row.querySelector('.subfeature_img, .hostiko_features_img'),
  );

  applicationRows.forEach((row) => {
    row.classList.add('solutions-applications-row');

    const applicationsHeading = cleanText(row.querySelector('h2, h3')?.textContent);
    if (applicationsHeading.toLowerCase() === 'digital display advantages') {
      row.classList.add('solutions-applications-row--display-advantages');
    }

    Array.from(row.querySelectorAll('.vc_col-sm-1')).forEach((spacer) => {
      if (!spacer.querySelector('img') && !spacer.querySelector('.solutions-feature-grid')) {
        spacer.remove();
      }
    });

    const cardsColumn = Array.from(row.querySelectorAll('.wpb_column')).find((column) =>
      column.querySelector('.solutions-feature-grid'),
    );
    const mediaColumn = row.querySelector('.subfeature_img, .hostiko_features_img');

    const mediaImage = mediaColumn?.querySelector('img');

    if (mediaColumn && mediaImage) {
      const src = mediaImage.getAttribute('src') || '';
      const alt = cleanText(mediaImage.getAttribute('alt')) || 'Solutions applications image';

      if (src) {
        const wrapper = mediaColumn.querySelector('.wpb_wrapper');

        if (wrapper) {
          wrapper.innerHTML = createSolutionsApplicationsMediaMarkup({ src, alt });
        }
      }
    }

    cardsColumn?.classList.add('solutions-applications-row__cards');
    mediaColumn?.classList.add('solutions-applications-row__media');
  });

  const featureSummarySections = Array.from(documentRoot.querySelectorAll('.vc_row')).filter((row) => {
    const heading = row.querySelector('h2, h3');
    const headingText = cleanText(heading?.textContent);

    return /^Key\b.*\bFeatures$/i.test(headingText);
  });

  featureSummarySections.forEach((row) => {
    row.classList.add('solutions-features-summary-section');
  });

  const visitorFeaturesSection = Array.from(documentRoot.querySelectorAll('.vc_row')).find((row) => {
    const heading = cleanText(row.querySelector('h2, h3')?.textContent);
    return heading.toLowerCase() === 'key visitor features';
  });

  visitorFeaturesSection?.classList.add('solutions-visitor-features-section');

  const ctaSections = Array.from(documentRoot.querySelectorAll('#call_of_action, .gredient_bg_orange')).filter(
    (section) => section.querySelector('h3') && section.querySelector('a[href]'),
  );

  ctaSections.forEach((section) => {
    const title = cleanText(section.querySelector('h3')?.textContent);
    const body = cleanText(
      section.querySelector('h4')?.textContent ||
      section.querySelector('p')?.textContent ||
      section.querySelector('li')?.textContent ||
      '',
    );
    const link = section.querySelector('a[href]');
    const href = link?.getAttribute('href') || '/contact-us/';
    const label = cleanText(link?.textContent) || 'Contact us';

    if (!title || !body) {
      return;
    }

    section.outerHTML = createSolutionsCtaMarkup({ title, body, href, label });
  });

  Array.from(documentRoot.querySelectorAll('a[href]')).forEach((link) => {
    const linkText = cleanText(link.textContent);

    if (!/^Are you an A\/V Integrator\?$/i.test(linkText)) {
      return;
    }

    link.classList.add('av-integrator-cta');
    link.closest('.vc_message_box')?.classList.add('av-integrator-box');
  });

  Array.from(documentRoot.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span')).forEach((element) => {
    if (cleanText(element.textContent) === 'Have Additional Question?') {
      element.textContent = 'Have Additional Questions?';
    }
  });
}

function tagClientLogoSections(documentRoot) {
  const headings = Array.from(documentRoot.querySelectorAll('h2, h3')).filter((heading) =>
    /clients?|client list/i.test(cleanText(heading.textContent)),
  );

  headings.forEach((heading) => {
    const sectionRow = heading.closest('.vc_row');
    sectionRow?.classList.add('solutions-clients-section');

    const siblings = Array.from(heading.parentElement?.children || []);
    const headingIndex = siblings.indexOf(heading);
    const container = siblings.slice(headingIndex + 1).find((node) => {
      if (!(node instanceof Element)) {
        return false;
      }

      const logos = Array.from(node.querySelectorAll('img')).filter((image) =>
        /client logo/i.test(cleanText(image.getAttribute('alt'))),
      );

      return logos.length >= 2;
    });

    if (container) {
      const logos = Array.from(container.querySelectorAll('img')).reduce((items, image) => {
        const src = image.getAttribute('src');
        const alt = cleanText(image.getAttribute('alt'));
        const link = image.closest('a[href]');
        const href = link?.getAttribute('href') || '';

        if (!src || !alt || !/client logo/i.test(alt)) {
          return items;
        }

        items.push({
          src,
          alt,
          href,
        });

        return items;
      }, []);

      if (logos.length >= 2) {
        container.outerHTML = createClientLogoStackMarkup(logos);
      } else {
        container.classList.add('client-logo-stack');
      }
    }
  });
}

function createClientLogoStackMarkup(logos) {
  const items = logos
    .map((logo) => {
      const image = `<img src="${escapeHtmlAttribute(logo.src)}" alt="${escapeHtmlAttribute(logo.alt)}" loading="eager" decoding="async" />`;

      return `
        <figure>
          ${logo.href ? `<a href="${escapeHtmlAttribute(logo.href)}">${image}</a>` : image}
        </figure>`;
    })
    .join('');

  return `<section class="client-logo-stack" aria-label="Client logos">${items}</section>`;
}

function transformAboutPage(documentRoot) {
  const obsoleteTeamMembers = new Set(['z anderson', 'regan boehm', 'alex canales']);
  const teamCards = Array.from(documentRoot.querySelectorAll('#team_inner .team_box'));

  teamCards.forEach((card) => {
    const name = cleanText(card.querySelector('h4')?.textContent).toLowerCase();
    if (obsoleteTeamMembers.has(name)) {
      card.remove();
      return;
    }

    card.classList.add('about-leadership-card');
    card.querySelector('.social_icons')?.remove();
  });

  const teamHeading = documentRoot.querySelector('h3#meet_header');
  const teamSubheading = documentRoot.querySelector('p#meet_header');
  const teamGrid = documentRoot.querySelector('#team_inner');

  if (teamHeading) {
    teamHeading.textContent = 'Leadership at Creating Margin';
  }

  if (teamSubheading) {
    teamSubheading.textContent = 'Meet the founder guiding Creating Margin\'s digital signage and workplace technology practice.';
  }

  if (teamGrid) {
    teamGrid.classList.add('team-grid');
    teamGrid.setAttribute('data-team-count', String(teamGrid.children.length));
  }

  const storyHeading = Array.from(documentRoot.querySelectorAll('h1')).find(
    (heading) => cleanText(heading.textContent) === 'What Creating Margin Means to Us',
  );
  const storySection = storyHeading?.closest('.vc_row');

  if (storySection) {
    storySection.classList.add('about-story-section');
  }

  const processHeading = Array.from(documentRoot.querySelectorAll('h3')).find(
    (heading) => cleanText(heading.textContent) === 'Our Three Step Process',
  );
  const processSection = processHeading?.closest('.vc_row');

  if (processSection) {
    processSection.classList.add('about-process-section');
    processSection.classList.remove('bbhd-hide-on-mobile');
  }

  const missionVisionIntro = Array.from(documentRoot.querySelectorAll('h3')).find(
    (heading) => cleanText(heading.textContent) === 'Understanding Our Vision and Our Mission',
  )?.closest('.vc_row');

  if (missionVisionIntro) {
    missionVisionIntro.classList.add('about-mission-intro');
  }

  const aboutCtaSection = Array.from(documentRoot.querySelectorAll('#call_of_action, .gredient_bg_orange')).find(
    (section) => cleanText(section.querySelector('h3')?.textContent) === 'Want to get to know more about us?',
  );

  if (aboutCtaSection) {
    const ctaTitle = cleanText(aboutCtaSection.querySelector('h3')?.textContent);
    const ctaBody = cleanText(aboutCtaSection.querySelector('h4')?.textContent);
    const ctaLink = aboutCtaSection.querySelector('a');
    const ctaHref = ctaLink?.getAttribute('href') || '/contact-us/';
    const ctaLabel = cleanText(ctaLink?.textContent) || 'Contact us';

    aboutCtaSection.outerHTML = createAboutCtaMarkup({
      title: ctaTitle,
      body: ctaBody,
      href: ctaHref,
      label: ctaLabel,
    });
  }

  const statementHeadings = Array.from(documentRoot.querySelectorAll('h2')).filter((heading) => {
    const text = cleanText(heading.textContent);
    return text === 'Our Mission' || text === 'Our Vision';
  });

  const aboutStatementCards = statementHeadings.reduce((cards, heading) => {
    const statementSection = heading.closest('.vc_row[data-vc-full-width="true"]') || heading.closest('.vc_row');
    const copy = cleanText(
      heading.closest('.wpb_wrapper')?.querySelector('p')?.textContent || '',
    );
    const image = Array.from(statementSection?.querySelectorAll('img') || []).sort((left, right) => {
      const leftArea = Number(left.getAttribute('width') || 0) * Number(left.getAttribute('height') || 0);
      const rightArea = Number(right.getAttribute('width') || 0) * Number(right.getAttribute('height') || 0);

      return rightArea - leftArea;
    })[0];

    if (!statementSection || !copy || !image) {
      return cards;
    }

    cards.push({
      section: statementSection,
      title: cleanText(heading.textContent),
      copy,
      imageSrc: image.getAttribute('src') || '',
      imageAlt: image.getAttribute('alt') || cleanText(heading.textContent),
    });

    return cards;
  }, []);

  if (aboutStatementCards.length > 0) {
    aboutStatementCards.forEach(({ section }) => {
      section.classList.add('about-statement-section');
    });
  }

  if (aboutStatementCards.length >= 2) {
    const [firstCard, ...remainingCards] = aboutStatementCards;
    firstCard.section.outerHTML = createAboutStatementCardsMarkup(aboutStatementCards);
    remainingCards.forEach(({ section }) => section.remove());
  }

  const volunteerSection = Array.from(documentRoot.querySelectorAll('h2')).find(
    (heading) => cleanText(heading.textContent) === 'Volunteerism in our DNA',
  )?.closest('.vc_row');

  if (volunteerSection) {
    volunteerSection.classList.add('volunteer-highlight');

    const volunteerImages = Array.from(volunteerSection.querySelectorAll('.vc_images_carousel img')).reduce((images, image) => {
      const source = image.getAttribute('src');

      if (!source || images.some((entry) => entry.src === source)) {
        return images;
      }

      images.push({
        src: source,
        alt: image.getAttribute('alt') || 'Creating Margin volunteer activity',
      });

      return images;
    }, []);

    const volunteerColumn = volunteerSection.querySelector('.vc_col-sm-5');

    if (volunteerColumn && volunteerImages.length > 0) {
      volunteerColumn.classList.remove('vc_hidden-xs');

      const volunteerWrapper = volunteerColumn.querySelector('.wpb_wrapper');

      if (volunteerWrapper) {
        volunteerWrapper.innerHTML = createVolunteerCarouselMarkup(volunteerImages);
      }
    }

    const volunteerImpactColumn = volunteerSection.querySelector('.vc_progress_bar')?.closest('.wpb_column');

    if (volunteerImpactColumn) {
      volunteerImpactColumn.classList.remove('vc_hidden-xs');
      volunteerImpactColumn.classList.add('about-volunteer-impact-column');

      const impactWrapper = volunteerImpactColumn.querySelector('.wpb_wrapper');

      if (impactWrapper) {
        impactWrapper.innerHTML = createVolunteerImpactMarkup({
          totalHours: 2015,
          years: 15,
          averageHoursPerYear: 134,
          annualGoalHours: 160,
        });
      }
    }
  }
}

function createVolunteerCarouselMarkup(images) {
  const slides = images
    .map(
      (image, index) => `
        <figure class="about-volunteer-slide${index === 0 ? ' is-active' : ''}" aria-hidden="${index === 0 ? 'false' : 'true'}">
          <img src="${image.src}" alt="${escapeHtmlAttribute(image.alt)}" loading="lazy" />
        </figure>`,
    )
    .join('');

  const dots = images
    .map(
      (_, index) => `
        <button type="button" class="about-volunteer-dot${index === 0 ? ' is-active' : ''}" aria-label="Show volunteer photo ${index + 1}" aria-current="${index === 0 ? 'true' : 'false'}"></button>`,
    )
    .join('');

  return `
    <div class="about-volunteer-carousel" data-slide-count="${images.length}">
      <div class="about-volunteer-track">${slides}
      </div>
      <button type="button" class="about-volunteer-control about-volunteer-control--prev" aria-label="Previous volunteer photo">‹</button>
      <button type="button" class="about-volunteer-control about-volunteer-control--next" aria-label="Next volunteer photo">›</button>
      <div class="about-volunteer-dots">${dots}
      </div>
    </div>`;
}

function createAboutStatementCardsMarkup(cards) {
  const items = cards
    .map(
      (card) => `
        <article class="about-statement-card">
          <div class="about-statement-card__copy">
            <h2>${escapeHtmlAttribute(card.title)}</h2>
            <p>${escapeHtmlAttribute(card.copy)}</p>
          </div>
          <figure class="about-statement-card__media">
            <img src="${escapeHtmlAttribute(card.imageSrc)}" alt="${escapeHtmlAttribute(card.imageAlt)}" loading="lazy" />
          </figure>
        </article>`,
    )
    .join('');

  return `
    <section class="about-statements-grid" aria-label="Mission and vision statements">
      ${items}
    </section>`;
}

function createSolutionsFeatureGridMarkup(cards) {
  const items = cards
    .map(
      (card) => `
        <article class="solutions-feature-card">
          <div class="solutions-feature-card__icon">
            ${card.iconSrc ? `<img src="${escapeHtmlAttribute(card.iconSrc)}" alt="${escapeHtmlAttribute(card.iconAlt)}" loading="lazy" />` : ''}
          </div>
          <h4>${escapeHtmlAttribute(card.title)}</h4>
          <p>${escapeHtmlAttribute(card.body)}</p>
        </article>`,
    )
    .join('');

  return `
    <section class="solutions-feature-grid" aria-label="Solutions features">
      ${items}
    </section>`;
}

function createProfessionalPlansGridMarkup(plans) {
  const items = plans
    .map((plan) => {
      const pricingCopy = plan.pricingCopy
        .map((line, index) => `<p class="professional-plan-card__pricing${index === plan.pricingCopy.length - 1 ? ' professional-plan-card__pricing--emphasis' : ''}">${escapeHtmlAttribute(line)}</p>`)
        .join('');

      const details = plan.details
        .map(
          (detail) => `
            <li class="professional-plan-card__detail">
              ${detail.iconSrc ? `<img src="${escapeHtmlAttribute(detail.iconSrc)}" alt="${escapeHtmlAttribute(detail.iconAlt)}" loading="lazy" />` : ''}
              <div>
                ${detail.title ? `<strong>${escapeHtmlAttribute(detail.title)}</strong>` : ''}
                ${detail.body ? `<span>${escapeHtmlAttribute(detail.body)}</span>` : ''}
              </div>
            </li>`,
        )
        .join('');

      return `
        <article class="professional-plan-card">
          <div class="professional-plan-card__header">
            <h3>${escapeHtmlAttribute(plan.title)}</h3>
            ${plan.badge ? `<p class="professional-plan-card__badge">${escapeHtmlAttribute(plan.badge)}</p>` : ''}
          </div>
          <div class="professional-plan-card__body">
            ${plan.price ? `<p class="professional-plan-card__price">${escapeHtmlAttribute(plan.price)}</p>` : ''}
            ${pricingCopy}
            ${details ? `<ul class="professional-plan-card__details">${details}</ul>` : ''}
          </div>
          <a class="professional-plan-card__button" href="${escapeHtmlAttribute(plan.href)}">${escapeHtmlAttribute(plan.label)}</a>
        </article>`;
    })
    .join('');

  return `
    <section class="professional-plans-grid" aria-label="Professional service plans">
      ${items}
    </section>`;
}

function createSolutionsApplicationsMediaMarkup({ src, alt }) {
  return `
    <figure class="solutions-applications-media" aria-label="Applications media">
      <img src="${escapeHtmlAttribute(src)}" alt="${escapeHtmlAttribute(alt)}" loading="lazy" decoding="async" />
    </figure>`;
}

function createDigitalSignageShowcaseGalleryMarkup() {
  const images = [
    {
      src: '/wp-content/uploads/2020/03/Creating_Margin_Digital_Signage_Apps-1024x684.jpg',
      alt: 'Digital signage application showcase',
    },
    {
      src: '/wp-content/uploads/2020/03/Creating_Margin_Digital_Signage_Software-1-1024x683.jpg',
      alt: 'Digital signage software in use',
    },
    {
      src: '/wp-content/uploads/2020/03/book-a-room_app-1024x683.jpg',
      alt: 'Room booking signage display',
    },
    {
      src: '/wp-content/uploads/2020/01/CUHealth_Door_Screen.jpg',
      alt: 'Door screen signage installation',
    },
    {
      src: '/wp-content/uploads/2020/03/1600-Broadway-Video-Wall-1140x580-1.jpg',
      alt: 'Video wall deployment',
    },
    {
      src: '/wp-content/uploads/2020/10/illegal-petes-digital-menu.jpg',
      alt: 'Restaurant digital menu board',
    },
  ];

  const items = images
    .map(
      (image) => `
        <figure class="solutions-showcase-gallery__item">
          <a class="solutions-showcase-gallery__link" href="${escapeHtmlAttribute(image.src)}" data-lightbox-alt="${escapeHtmlAttribute(image.alt)}" aria-label="Open image: ${escapeHtmlAttribute(image.alt)}">
            <img src="${escapeHtmlAttribute(image.src)}" alt="${escapeHtmlAttribute(image.alt)}" loading="lazy" decoding="async" />
          </a>
        </figure>`,
    )
    .join('');

  return `<div class="solutions-showcase-gallery" aria-label="Digital signage gallery">${items}</div>`;
}

function createSolutionsCtaMarkup({ title, body, href, label }) {
  return `
    <section class="solutions-cta-banner" aria-label="Call to action">
      <div class="solutions-cta-banner__copy">
        <h3>${escapeHtmlAttribute(title)}</h3>
        <p>${escapeHtmlAttribute(body)}</p>
      </div>
      <a class="solutions-cta-banner__button" href="${escapeHtmlAttribute(href)}">${escapeHtmlAttribute(label)}</a>
    </section>`;
}

function createAboutCtaMarkup({ title, body, href, label }) {
  return `
    <section class="about-cta-options" aria-label="Contact call to action">
      <article class="about-cta-option about-cta-option--navy">
        <div class="about-cta-option__copy">
          <h3>${escapeHtmlAttribute(title)}</h3>
          <p>${escapeHtmlAttribute(body)}</p>
        </div>
        <a class="about-cta-option__button" href="${escapeHtmlAttribute(href)}">${escapeHtmlAttribute(label)}</a>
      </article>
    </section>`;
}

function createVolunteerImpactMarkup({ totalHours, years, averageHoursPerYear, annualGoalHours }) {
  const completion = Math.min(100, Math.round((averageHoursPerYear / annualGoalHours) * 100));

  return `
    <section class="about-volunteer-impact" aria-label="Volunteer time summary">
      <div class="about-volunteer-impact__header">
        <p class="about-volunteer-impact__eyebrow">Community Impact</p>
        <div class="about-volunteer-impact__headline">
          <strong>${totalHours.toLocaleString()}</strong>
          <span>volunteer hours over ${years} years</span>
        </div>
      </div>
      <div class="about-volunteer-impact__metrics">
        <div class="about-volunteer-impact__metric">
          <span class="about-volunteer-impact__metric-value">${averageHoursPerYear}</span>
          <span class="about-volunteer-impact__metric-label">avg. hours each year</span>
        </div>
        <div class="about-volunteer-impact__metric">
          <span class="about-volunteer-impact__metric-value">4x</span>
          <span class="about-volunteer-impact__metric-label">service projects each year</span>
        </div>
        <div class="about-volunteer-impact__metric">
          <span class="about-volunteer-impact__metric-value">${years}</span>
          <span class="about-volunteer-impact__metric-label">years giving back</span>
        </div>
      </div>
      <div class="about-volunteer-impact__progress" aria-hidden="true">
        <div class="about-volunteer-impact__progress-copy">
          <span>Average yearly contribution</span>
          <strong>${completion}% of annual target</strong>
        </div>
        <div class="about-volunteer-impact__track">
          <span class="about-volunteer-impact__fill" style="width: ${completion}%"></span>
        </div>
      </div>
    </section>`;
}

function escapeHtmlAttribute(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function pruneEmptyMigratedRows(documentRoot) {
  const meaningfulSelectors = [
    'img',
    'video',
    'iframe',
    'svg',
    'table',
    'form',
    '.vc_btn3',
    '.wpb_single_image',
    '.wpb_video_wrapper',
    '.vc_progress_bar',
    '.vc_images_carousel',
    '.wpb_gallery',
    '.vc_toggle',
    '.wpb_gmaps_widget',
    '.wpcf7',
    'ul li',
    'ol li',
  ].join(',');

  const hasMeaningfulContent = (element) => {
    if (!element) {
      return false;
    }

    if (cleanText(element.textContent)) {
      return true;
    }

    return Boolean(element.querySelector(meaningfulSelectors));
  };

  Array.from(documentRoot.querySelectorAll('.wpb_text_column')).forEach((block) => {
    if (hasMeaningfulContent(block)) {
      return;
    }

    const row = block.closest('.vc_row');
    block.remove();

    if (!row) {
      return;
    }

    const directColumns = Array.from(row.children).filter((child) => child.classList.contains('wpb_column'));
    if (directColumns.length > 0 && directColumns.every((column) => !hasMeaningfulContent(column))) {
      row.remove();
    }
  });
}

function useSeoMetadata({ locationPathname, siteData, currentPath, currentEntry, ppcLandingPage, aiConsultingPage, hasError }) {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const siteName = cleanText(siteData?.site?.title) || HOME_FALLBACK.title;
    const siteDescription = cleanText(siteData?.site?.description) || HOME_FALLBACK.description;
    const seoOrigin = resolveSeoOrigin(siteData?.site?.origin);
    const canonicalPath = normalizePath(locationPathname || currentPath || '/');
    const canonicalUrl = `${seoOrigin}${canonicalPath === '/' ? '/' : `${canonicalPath}/`}`;

    let title = `${siteName} | ${siteDescription}`;
    let description = siteDescription;
    let ogType = 'website';
    let robots = 'index,follow';
    let image = '/wp-content/uploads/2018/12/Creating-Margin.png';

    if (hasError) {
      title = `Content Load Error | ${siteName}`;
      description = 'There was an issue loading this page.';
      robots = 'noindex,nofollow';
    } else if (ppcLandingPage) {
      title = `${ppcLandingPage.title} | ${siteName}`;
      description = cleanText(ppcLandingPage.subtitle) || siteDescription;
      image = ppcLandingPage.heroImage || image;
    } else if (aiConsultingPage) {
      title = `${aiConsultingPage.title} | ${siteName}`;
      description = cleanText(aiConsultingPage.subtitle) || siteDescription;
    } else if (currentEntry) {
      const entryTitle = cleanText(currentEntry.title);
      const entrySummary = getSeoSummary(currentEntry);

      if (currentEntry.path === '/') {
        title = `${siteName} | ${siteDescription}`;
        description = entrySummary || siteDescription;
      } else {
        title = `${entryTitle || siteName} | ${siteName}`;
        description = entrySummary || siteDescription;
      }

      if (currentEntry.kind === 'post') {
        ogType = 'article';
      }

      const bannerBackground = getMigratedPageBackground(currentEntry.path);
      image = bannerBackground?.image || image;
    } else {
      title = `Page Not Found | ${siteName}`;
      description = 'The requested page could not be found.';
      robots = 'noindex,nofollow';
    }

    document.title = title;

    upsertHeadTag('meta[name="description"]', 'meta', {
      name: 'description',
      content: description,
    });

    upsertHeadTag('meta[name="robots"]', 'meta', {
      name: 'robots',
      content: robots,
    });

    upsertHeadTag('link[rel="canonical"]', 'link', {
      rel: 'canonical',
      href: canonicalUrl,
    });

    upsertHeadTag('meta[property="og:site_name"]', 'meta', {
      property: 'og:site_name',
      content: siteName,
    });
    upsertHeadTag('meta[property="og:title"]', 'meta', {
      property: 'og:title',
      content: title,
    });
    upsertHeadTag('meta[property="og:description"]', 'meta', {
      property: 'og:description',
      content: description,
    });
    upsertHeadTag('meta[property="og:type"]', 'meta', {
      property: 'og:type',
      content: ogType,
    });
    upsertHeadTag('meta[property="og:url"]', 'meta', {
      property: 'og:url',
      content: canonicalUrl,
    });
    upsertHeadTag('meta[property="og:image"]', 'meta', {
      property: 'og:image',
      content: toAbsoluteUrl(image, seoOrigin),
    });

    upsertHeadTag('meta[name="twitter:card"]', 'meta', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    upsertHeadTag('meta[name="twitter:title"]', 'meta', {
      name: 'twitter:title',
      content: title,
    });
    upsertHeadTag('meta[name="twitter:description"]', 'meta', {
      name: 'twitter:description',
      content: description,
    });
    upsertHeadTag('meta[name="twitter:image"]', 'meta', {
      name: 'twitter:image',
      content: toAbsoluteUrl(image, seoOrigin),
    });

    const routeLcpPreloadSelector = 'link[data-route-lcp-preload="true"]';
    const routeLcpPreloadNode = document.head?.querySelector(routeLcpPreloadSelector);
    if (currentEntry?.path === '/') {
      const imagePath = toAbsoluteUrl(image, seoOrigin);
      upsertHeadTag(routeLcpPreloadSelector, 'link', {
        rel: 'preload',
        as: 'image',
        href: imagePath,
        fetchpriority: 'high',
        'data-route-lcp-preload': 'true',
      });
    } else if (routeLcpPreloadNode) {
      routeLcpPreloadNode.remove();
    }

    const schema = {
      '@context': 'https://schema.org',
      '@type': currentEntry?.kind === 'post' ? 'Article' : 'WebPage',
      name: title,
      description,
      url: canonicalUrl,
      publisher: {
        '@type': 'Organization',
        name: siteName,
      },
    };

    if (currentEntry?.kind === 'post') {
      schema.datePublished = currentEntry.date || undefined;
      schema.dateModified = currentEntry.modified || currentEntry.date || undefined;
    }

    upsertJsonLd('route-seo-schema', schema);
  }, [locationPathname, siteData, currentPath, currentEntry, ppcLandingPage, aiConsultingPage, hasError]);
}

function resolveSeoOrigin(siteOrigin) {
  const envOrigin = cleanText(import.meta.env.VITE_SITE_ORIGIN || '');
  const configuredOrigin = cleanText(siteOrigin || '');
  const fallbackOrigin = 'https://creatingmargin.com';
  const candidate = envOrigin || configuredOrigin || fallbackOrigin;

  if (/^https?:\/\//i.test(candidate)) {
    return candidate.replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && /^https?:\/\//i.test(window.location.origin)) {
    return window.location.origin.replace(/\/+$/, '');
  }

  return fallbackOrigin;
}

function toAbsoluteUrl(value, origin) {
  const source = cleanText(value || '');
  if (!source) {
    return source;
  }

  if (/^https?:\/\//i.test(source)) {
    return source;
  }

  const base = String(origin || '').replace(/\/+$/, '');
  const path = source.startsWith('/') ? source : `/${source}`;
  return `${base}${path}`;
}

function upsertHeadTag(selector, tagName, attributes) {
  const head = document.head;
  if (!head) {
    return;
  }

  let node = head.querySelector(selector);

  if (!node) {
    node = document.createElement(tagName);
    head.appendChild(node);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value == null || value === '') {
      return;
    }

    node.setAttribute(key, String(value));
  });
}

function upsertJsonLd(id, data) {
  const head = document.head;
  if (!head) {
    return;
  }

  let node = head.querySelector(`script[data-seo-id="${id}"]`);

  if (!node) {
    node = document.createElement('script');
    node.setAttribute('type', 'application/ld+json');
    node.setAttribute('data-seo-id', id);
    head.appendChild(node);
  }

  node.textContent = JSON.stringify(data);
}

function getSeoSummary(entry) {
  const excerpt = cleanText(entry?.excerpt || '');
  if (excerpt) {
    return excerpt;
  }

  const html = entry?.html || '';
  if (!html) {
    return '';
  }

  if (typeof DOMParser !== 'undefined') {
    const parser = new DOMParser();
    const documentRoot = parser.parseFromString(html, 'text/html');
    const firstParagraph = cleanText(documentRoot.querySelector('p')?.textContent || '');

    if (firstParagraph) {
      return firstParagraph.slice(0, 155);
    }
  }

  return cleanText(String(html).replace(/<[^>]+>/g, ' ')).slice(0, 155);
}

function getMigratedPageClasses(path) {
  const classes = ['home-surface', 'service-surface', 'content-html', 'page-surface'];

  if (path === '/about-us') {
    classes.push('page-surface--about');
  }

  if (path.startsWith('/contact-us')) {
    classes.push('page-surface--contact');
  }

  if (path.startsWith('/professional-services')) {
    classes.push('page-surface--services');
  }

  if (path.startsWith('/digital-signage') || path.startsWith('/workplace-management') || path.startsWith('/contivision')) {
    classes.push('page-surface--solutions');
  }

  return classes.join(' ');
}

function getMigratedPageStyle(path) {
  const background = getMigratedPageBackground(path);

  if (!background) {
    return undefined;
  }

  return {
    '--page-banner-image': `url("${background.image}")`,
    '--page-banner-position': background.position || '50% 50%',
  };
}

function getMigratedPageBackground(path) {
  const normalizedPath = normalizePath(path);

  if (PAGE_HEADER_BACKGROUNDS[normalizedPath]) {
    return PAGE_HEADER_BACKGROUNDS[normalizedPath];
  }

  return (
    PAGE_HEADER_FALLBACKS.find((entry) => normalizedPath.startsWith(entry.prefix)) || null
  );
}

function buildPortfolioSummary(item, categoryLabels) {
  if (item.categories.length === 0) {
    return 'Open the full example to review this Creating Margin portfolio item.';
  }

  const labels = item.categories.map((category) => categoryLabels.get(category) || formatSlug(category));
  return `Example work for ${labels.join(', ')} applications.`;
}

function getPageWindow(totalPages, currentPage) {
  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, start + 4);
  const adjustedStart = Math.max(1, end - 4);
  const pages = [];

  for (let page = adjustedStart; page <= end; page += 1) {
    pages.push(page);
  }

  return pages;
}

function cleanText(value) {
  return value?.replace(/\s+/g, ' ').trim() || '';
}

function prettifyPortfolioTitle(value) {
  const normalized = value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized || value;
}

function formatSlug(value) {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatDate(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function normalizePath(value) {
  if (!value || value === '/') {
    return '/';
  }

  const normalized = value.endsWith('/') ? value.slice(0, -1) : value;

  if (normalized.toLowerCase() === '/home') {
    return '/';
  }

  return normalized;
}

function sanitizeLegacyPostHtml(html) {
  let output = String(html || '');

  output = output.replace(/<nav[^>]*class=(['"])[^'"]*post-navigation[^'"]*\1[^>]*>[\s\S]*?<\/nav>/gi, '');
  output = output.replace(/<h[1-6][^>]*>\s*Post navigation\s*<\/h[1-6]>\s*(?:<p>[\s\S]*?<\/p>)?/gi, '');
  output = output.replace(
    /<header[^>]*class=(['"])[^'"]*entry-header[^'"]*\1[^>]*>[\s\S]*?author by[\s\S]*?<\/header>/gi,
    '',
  );
  output = output.replace(/<p[^>]*>\s*-\s*<\/p>/gi, '');
  output = output.replace(/<li[^>]*>\s*(?:&bull;|•)?\s*<\/li>/gi, '');
  output = output.replace(/<(ul|ol)[^>]*>\s*<\/(ul|ol)>/gi, '');

  return output;
}

function inferImageDimensionsFromUrl(url) {
  const source = String(url || '').trim();
  if (!source) {
    return null;
  }

  const normalized = source.split('?')[0].toLowerCase();
  const knownDimensionMap = {
    '/wp-content/uploads/2018/12/creating-margin.png': { width: 220, height: 74 },
    '/wp-content/uploads/2020/05/digital-signage-banner.webp': { width: 1200, height: 675 },
  };

  if (knownDimensionMap[normalized]) {
    return knownDimensionMap[normalized];
  }

  const tokenMatches = normalized.match(/(?:^|[-_])(\d{2,4})x(\d{2,4})(?:[^0-9]|$)/);
  if (tokenMatches) {
    const width = Number.parseInt(tokenMatches[1], 10);
    const height = Number.parseInt(tokenMatches[2], 10);

    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      return { width, height };
    }
  }

  return null;
}

function getResponsiveImageAttrs(src, sizes = '100vw') {
  const source = String(src || '').trim();
  if (!source) {
    return { srcSet: undefined, sizes };
  }

  const dimensions = inferImageDimensionsFromUrl(source);
  if (!dimensions?.width || !dimensions?.height) {
    return { srcSet: undefined, sizes };
  }

  const urlWithoutQuery = source.split('?')[0];
  const query = source.includes('?') ? source.slice(source.indexOf('?')) : '';
  const extensionMatch = urlWithoutQuery.match(/\.(avif|webp|png|jpe?g)$/i);
  if (!extensionMatch) {
    return { srcSet: undefined, sizes };
  }

  const extension = extensionMatch[0];
  const basePath = urlWithoutQuery.slice(0, -extension.length);
  const hasInlineDimensionToken = /(?:^|[-_])(\d{2,4})x(\d{2,4})$/i.test(basePath);
  const isExplicitlyMappedAsset =
    normalizedPath(urlWithoutQuery) === '/wp-content/uploads/2020/05/digital-signage-banner.webp';

  if (!hasInlineDimensionToken && !isExplicitlyMappedAsset) {
    return { srcSet: undefined, sizes };
  }

  const aspectRatio = dimensions.height / dimensions.width;
  const candidateWidths = [320, 480, 640, 768, 960, 1200, 1440]
    .filter((width) => width < dimensions.width)
    .slice(-4);

  if (candidateWidths.length === 0) {
    return { srcSet: undefined, sizes };
  }

  const srcSetParts = candidateWidths.map((width) => {
    const height = Math.max(1, Math.round(width * aspectRatio));
    return `${basePath}-${width}x${height}${extension}${query} ${width}w`;
  });

  srcSetParts.push(`${source} ${dimensions.width}w`);

  return {
    srcSet: srcSetParts.join(', '),
    sizes,
  };
}

function normalizedPath(value) {
  return String(value || '').trim().split('?')[0].toLowerCase();
}

function extractPreferredImageSrcSet(imageNode) {
  if (!imageNode) {
    return undefined;
  }

  const candidates = [
    cleanText(imageNode.getAttribute('data-srcset')),
    cleanText(imageNode.getAttribute('srcset')),
  ];

  for (const value of candidates) {
    if (!value) {
      continue;
    }

    if (value.toLowerCase().startsWith('data:image/')) {
      continue;
    }

    if (value.includes('/wp-content/uploads/')) {
      return value.replace(/\s*,\s*/g, ', ');
    }
  }

  return undefined;
}